<template>
  <section class="others-page">
    <div class="mb-2">
      <button
        type="button"
        class="back-btn"
        @click="goBack">
        <b-icon icon="arrow-left" />
        <span>Back to Client Logs</span>
      </button>
    </div>

    <nav class="details-tabs" aria-label="Hotel information sections">
      <div class="tab-buttons" role="tablist">
        <button
          id="hotel-details-tab"
          type="button"
          role="tab"
          :aria-selected="activeTab === 'hotel' ? 'true' : 'false'"
          aria-controls="hotel-details-panel"
          :class="{ active: activeTab === 'hotel' }"
          @click="activeTab = 'hotel'"
        >
          <b-icon icon="building" />
          <span><strong>Hotel Details</strong></span>
        </button>
        <button
          id="room-details-tab"
          type="button"
          role="tab"
          :aria-selected="activeTab === 'room' ? 'true' : 'false'"
          aria-controls="room-details-panel"
          :class="{ active: activeTab === 'room' }"
          @click="activeTab = 'room'"
        >
          <b-icon icon="door-open" />
          <span><strong>Room Details</strong></span>
        </button>
        <button
          id="valuation-tab"
          type="button"
          role="tab"
          :aria-selected="activeTab === 'valuation' ? 'true' : 'false'"
          aria-controls="valuation-panel"
          :class="{ active: activeTab === 'valuation' }"
          @click="activeTab = 'valuation'"
        >
          <b-icon icon="calculator" />
          <span><strong>Valuation</strong></span>
        </button>
      </div>
      <label class="tab-language-picker">
        <span><b-icon icon="translate" /> Language</span>
        <select v-model="selectedLanguage" aria-label="Display language">
          <option v-for="language in availableLanguages" :key="language" :value="language">
            {{ languageLabel(language) }} ({{ language }})
          </option>
        </select>
      </label>
    </nav>

    <div
      v-show="activeTab === 'room'"
      id="room-details-panel"
      class="others-page-card"
      role="tabpanel"
      aria-labelledby="room-details-tab"
    >
      <div
        v-if="pageLoading"
        class="others-skeleton"
        aria-label="Loading Others summary">
        <div class="others-skeleton-card" />
        <div
          v-for="index in 4"
          :key="index"
          class="others-skeleton-row" />
      </div>

      <div
        v-else-if="loadError"
        class="page-error">
        <b-icon icon="exclamation-circle" />
        <h4>Others Summary could not be loaded</h4>
        <p>{{ loadError }}</p>
        <button
          type="button"
          @click="loadPage">
          Try Again
        </button>
      </div>

      <div
        v-else
        class="others-layout">
        <header class="others-header">
          <div class="header-monogram">
            <b-icon icon="building" />
          </div>
          <div class="header-copy">
            <span class="page-eyebrow">Room Intelligence</span>
            <h2>Room Summary</h2>
            <p>{{ formatClientCell(activeOtherLog) }} <span>•</span> {{ formatCell(activeOtherLog.city) }}</p>
          </div>
          <div class="header-status">
            <i />
            Live report
          </div>
        </header>

        <div class="summary-strip">
          <div class="summary-chip">
            <b-icon icon="hash" />
            <span>Request ID</span>
            <strong>{{ formatCell(activeOtherLog.rid) }}</strong>
          </div>
          <div class="summary-chip">
            <b-icon icon="key" />
            <span>Session ID</span>
            <strong>{{ formatCell(activeOtherLog.ssid) }}</strong>
          </div>
          <div class="summary-chip">
            <b-icon icon="grid-3x3-gap" />
            <span>Group ID</span>
            <strong>{{ formatCell(activeOtherLog.group_id) }}</strong>
          </div>
          <div class="summary-chip">
            <b-icon icon="calendar3" />
            <span>Created At</span>
            <strong>{{ formatCell(activeOtherLog.create_date) }}</strong>
          </div>
          <div class="summary-chip total-chip">
            <b-icon icon="door-open" />
            <span>Total Rooms</span>
            <strong>{{ formatCell(activeOtherLog.row_count) }}</strong>
          </div>
        </div>

        <div class="collapse-card">
          <button
            type="button"
            class="collapse-strip"
            :aria-expanded="showOthersSummary ? 'true' : 'false'"
            @click="showOthersSummary = !showOthersSummary"
          >
            <div class="collapse-title">
              <b-icon icon="collection" />
              <span>Others</span>
            </div>
            <div class="collapse-meta">
              <span>Room Summary Others</span>
              <strong>{{ roomSummaryOthersDisplay }}</strong>
              <b-icon :icon="showOthersSummary ? 'chevron-up' : 'chevron-down'" />
            </div>
          </button>

          <div
            v-if="showOthersSummary"
            class="summary-table-wrap">
            <div class="table-topbar">
              <div>
                <h5>Other type count summary</h5>
                <p>`Others Count` = saved existing count, `Invalid Others Count` = data that should not have gone into Others.</p>
              </div>
              <span class="mini-badge">{{ otherRows.length }} Items</span>
            </div>

            <div class="table-scroll">
              <table class="summary-table">
                <thead>
                  <tr>
                    <th>Other Type</th>
                    <th>Others Count</th>
                    <th>Invalid Others Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in otherRows"
                    :key="item.key"
                    :class="{ active: selectedOtherKey === item.key }">
                    <td>
                      <div class="type-cell">
                        <span />{{ item.label }}
                      </div>
                    </td>
                    <td>
                      <button
                        v-b-tooltip.hover
                        type="button"
                        class="count-btn saved"
                        :class="{ active: isSelectedOtherDetail(item, 'others') }"
                        :disabled="item.originalCount <= 0 || detailsLoading"
                        :title="item.originalCount > 0 ? `View ${item.label} Others Count` : 'No Others Count data available'"
                        @click="toggleOtherDetails(item, 'others')"
                      >
                        <b-icon :icon="isSelectedOtherDetail(item, 'others') ? 'eye-fill' : 'eye'" />
                        <span>{{ item.originalDisplayCount }}</span>
                      </button>
                    </td>
                    <td>
                      <button
                        v-b-tooltip.hover
                        type="button"
                        class="count-btn invalid"
                        :class="{ active: isSelectedOtherDetail(item, 'invalid') }"
                        :disabled="item.count <= 0 || detailsLoading"
                        :title="item.count > 0 ? `View ${item.label} Invalid Others Count` : 'No Invalid Others Count data available'"
                        @click="toggleOtherDetails(item, 'invalid')"
                      >
                        <b-icon :icon="isSelectedOtherDetail(item, 'invalid') ? 'eye-fill' : 'eye'" />
                        <span>{{ item.displayCount }}</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div
          v-if="detailsLoading"
          class="detail-loading">
          <div
            v-for="index in 3"
            :key="index" />
        </div>

        <div
          v-else-if="selectedOtherKey && detailRows.length"
          class="detail-wrap">
          <div class="detail-topbar">
            <h5>{{ selectedOtherSummary.label }} {{ selectedOtherCountTypeLabel }} Data</h5>
            <p>{{ detailRows.length }} rows available for review.</p>
          </div>
          <div class="detail-scroll">
            <table class="detail-table">
              <thead>
                <tr>
                  <th
                    v-for="column in detailColumns"
                    :key="column">
                    {{ formatDetailColumn(column) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(detailRow, index) in detailRows"
                  :key="`${selectedOtherKey}-${index}`">
                  <td
                    v-for="column in detailColumns"
                    :key="`${selectedOtherKey}-${index}-${column}`">
                    {{ formatCell(detailRow[column]) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          v-else-if="selectedOtherKey"
          class="empty-state">
          <b-icon icon="inbox" />
          <h5>No data found</h5>
          <p>{{ selectedOtherSummary.label }} {{ selectedOtherCountTypeLabel }} has no rows for this query.</p>
        </div>

        <section class="classified-rooms-section">
          <header class="classified-section-header">
            <div class="classified-heading">
              <span class="classified-heading-icon">
                <b-icon icon="gem" />
              </span>
              <div>
                <span class="section-eyebrow">Classified rooms</span>
                <h3>Room Variants &amp; Summaries</h3>
                <p>Browse every room variant, its summaries and live pricing details.</p>
              </div>
            </div>
            <div class="classified-stats">
              <!-- <label class="language-picker">
                <select v-model="selectedLanguage" aria-label="Display language">
                  <option v-for="language in availableLanguages" :key="language" :value="language">
                    {{ languageLabel(language) }}
                  </option>
                </select>
              </label> -->
              <span><strong>{{ roomVariants.length }}</strong> Variants</span>
            </div>
          </header>

          <div
            v-if="classifiedRoomsLoading"
            class="variant-skeleton"
            aria-label="Loading classified rooms">
            <div
              v-for="index in 4"
              :key="index" />
          </div>

          <div
            v-else-if="classifiedRoomsError"
            class="classified-state error">
            <b-icon icon="exclamation-circle" />
            <div>
              <strong>Room variants could not be loaded</strong>
              <p>{{ classifiedRoomsError }}</p>
            </div>
            <button
              type="button"
              @click="loadRoomVariants">
              Try Again
            </button>
          </div>

          <div
            v-else-if="!roomVariants.length"
            class="classified-state">
            <b-icon icon="inbox" />
            <div>
              <strong>No room variants found</strong>
              <p>No non-empty Room Variant is available for this request session.</p>
            </div>
          </div>

          <div
            v-else
            class="variant-list">
            <article
              v-for="variant in roomVariants"
              :key="variant.room_variant"
              class="variant-card"
              :class="{ expanded: expandedVariant === variant.room_variant }">
              <button
                type="button"
                class="variant-trigger"
                :aria-expanded="expandedVariant === variant.room_variant ? 'true' : 'false'"
                @click="toggleRoomVariant(variant)">
                <span class="variant-toggle-icon">
                  <b-icon :icon="expandedVariant === variant.room_variant ? 'dash' : 'plus'" />
                </span>
                <span class="variant-name">
                  <span :dir="selectedLanguage === 'ar' ? 'rtl' : 'auto'">{{ translatedRoomField(variant, 'room_variant', variant.room_variant, ['room_variant']) }}</span>
                </span>
                <span class="variant-count">
                  <strong>{{ variant.room_summary_count }}</strong>
                  <span>{{ pluralize(variant.room_summary_count, 'Summary', 'Summaries') }}</span>
                </span>
                <b-icon
                  class="variant-chevron"
                  :icon="expandedVariant === variant.room_variant ? 'chevron-up' : 'chevron-down'" />
              </button>

              <div
                v-if="expandedVariant === variant.room_variant"
                class="variant-panel">
                <div
                  v-if="roomSummariesLoading"
                  class="room-table-skeleton">
                  <div
                    v-for="index in 3"
                    :key="index" />
                </div>

                <div
                  v-else-if="roomSummariesError"
                  class="variant-panel-state">
                  <b-icon icon="exclamation-circle" />
                  <span>{{ roomSummariesError }}</span>
                  <button
                    type="button"
                    @click="loadRoomSummaries">
                    Retry
                  </button>
                </div>

                <div
                  v-else-if="!roomSummaries.length"
                  class="variant-panel-state">
                  <b-icon icon="inbox" />
                  <span>No Room Summary found for this variant.</span>
                </div>

                <div
                  v-else
                  class="room-summary-list">
                  <article
                    v-for="summary in roomSummaries"
                    :key="summary.room_summary"
                    class="room-summary-card"
                    :class="{ expanded: expandedRoomSummary === summary.room_summary }">
                    <button
                      type="button"
                      class="room-summary-trigger"
                      :aria-expanded="expandedRoomSummary === summary.room_summary ? 'true' : 'false'"
                      @click="toggleRoomSummary(summary)">
                      <span class="summary-toggle-icon">
                        <b-icon :icon="expandedRoomSummary === summary.room_summary ? 'dash' : 'plus'" />
                      </span>
                      <span class="room-summary-name">
                        <span :dir="selectedLanguage === 'ar' ? 'rtl' : 'auto'">{{ translatedRoomField(summary, 'room_summary', summary.room_summary, ['room']) }}</span>
                      </span>
                      <strong class="room-count-badge">
                        <span>{{ summaryOptionCount(summary) }}</span>
                        {{ pluralize(summaryOptionCount(summary), 'Option', 'Options') }}
                      </strong>
                      <b-icon :icon="expandedRoomSummary === summary.room_summary ? 'chevron-up' : 'chevron-down'" />
                    </button>

                    <div
                      v-if="expandedRoomSummary === summary.room_summary"
                      class="room-summary-panel">
                      <div
                        v-if="variantRoomsLoading"
                        class="room-table-skeleton">
                        <div
                          v-for="index in 5"
                          :key="index" />
                      </div>

                      <div
                        v-else-if="variantRoomsError"
                        class="variant-panel-state">
                        <b-icon icon="exclamation-circle" />
                        <span>{{ variantRoomsError }}</span>
                        <button
                          type="button"
                          @click="loadVariantRooms">
                          Retry
                        </button>
                      </div>

                      <div
                        v-else-if="!roomOptions.length"
                        class="variant-panel-state">
                        <b-icon icon="inbox" />
                        <span>No room options found for this Room Summary.</span>
                      </div>

                      <template v-else>
                        <div
                          class="room-options"
                          aria-label="Grouped room options">
                          <article
                            v-for="(option, optionIndex) in roomOptions"
                            :key="optionKey(option)"
                            class="room-option-card">
                            <header class="option-header">
                              <div>
                                <span class="option-number">Option {{ optionNumber(optionIndex) }}</span>
                                <strong>{{ option.rooms.length }} {{ pluralize(option.rooms.length, 'room', 'rooms') }} together</strong>
                              </div>
                              <span class="supplier-reference">
                                <b-icon icon="building" />
                                {{ optionSupplier(option) }}
                              </span>
                            </header>

                            <div class="option-rooms">
                              <div
                                v-for="(room, roomIndex) in option.rooms"
                                :key="`${optionKey(option)}-${roomIndex}`"
                                class="option-room-row">
                                <span class="room-sequence">{{ roomIndex + 1 }}</span>
                                <div
                                  class="option-room-copy"
                                  :dir="selectedLanguage === 'ar' ? 'rtl' : 'auto'">
                                  <strong>{{ roomDisplayName(room) }}</strong>
                                  <span class="room-occupancy">{{ roomOccupancy(room) }}</span>
                                  <span class="room-attributes">
                                    <span class="room-attribute bed-attribute">
                                      <b>Bed type</b>
                                      {{ roomBedType(room) }}
                                    </span>
                                    <span class="room-attribute view-attribute">
                                      <b>View</b>
                                      {{ roomView(room) }}
                                    </span>
                                  </span>
                                </div>
                                <span
                                  class="meal-pill"
                                  :dir="selectedLanguage === 'ar' ? 'rtl' : 'auto'">
                                  <b-icon icon="cup-hot" />
                                  {{ translatedRoomField(room, 'meal', room.meal_basis, ['meal']) || '-' }}
                                </span>
                                <span
                                  class="policy-pill"
                                  :class="{ refundable: isRefundable(room.refund_policy) }"
                                  :dir="selectedLanguage === 'ar' ? 'rtl' : 'auto'">
                                  <i />
                                  {{ translatedRoomField(room, 'policy', formatPolicy(room.refund_policy), ['phrase']) }}
                                </span>
                                <strong class="individual-rate">{{ formatIndividualRoomRate(room, option) }}</strong>
                              </div>
                            </div>

                            <footer class="option-footer">
                              <span>Complete stay total</span>
                              <strong>{{ formatOptionRate(option) }}</strong>
                            </footer>
                          </article>
                        </div>

                        <div
                          v-if="variantPagination.total_pages > 1"
                          class="variant-pagination">
                          <button
                            type="button"
                            :disabled="variantPagination.page <= 1 || variantRoomsLoading"
                            @click="changeVariantPage(variantPagination.page - 1)">
                            <b-icon icon="chevron-left" />
                            Previous
                          </button>
                          <span>Page {{ variantPagination.page }} of {{ variantPagination.total_pages }}</span>
                          <button
                            type="button"
                            :disabled="variantPagination.page >= variantPagination.total_pages || variantRoomsLoading"
                            @click="changeVariantPage(variantPagination.page + 1)">
                            Next
                            <b-icon icon="chevron-right" />
                          </button>
                        </div>
                      </template>
                    </div>
                  </article>
                </div>
              </div>
            </article>
          </div>
        </section>
      </div>
    </div>

    <div
      v-show="activeTab === 'hotel'"
      id="hotel-details-panel"
      class="hotel-tab-card"
      role="tabpanel"
      aria-labelledby="hotel-details-tab"
    >
      <hotel-details-panel
        :supplier-code="groupId"
        :supplier="hotelSupplier"
        :language="selectedLanguage"
      />
    </div>

    <ValuationTab
      v-if="activeTab === 'valuation'"
      id="valuation-panel"
      role="tabpanel"
      aria-labelledby="valuation-tab"
      :language="selectedLanguage"
      :sample-index="valuationSampleIndex"
    />
  </section>
</template>

<script>
import axios from '@axios'
import {
  BIcon,
  VBTooltip,
} from 'bootstrap-vue'
import ValuationTab from '../../components/ValuationTab.vue'
import valuationSamples from '../../data/valuationSamples'
import HotelDetailsPanel from './HotelDetailsPanel.vue'
import { getRoomMappingOthersContext } from './roomMappingOthersContext'

const EMPTY_COUNTS = {
  bed_type_others: 0,
  meals_others: 0,
  view_others: 0,
  room_summary_others: 0,
}

export default {
  name: 'RoomMappingOthersSummary',
  components: {
    BIcon,
    HotelDetailsPanel,
    ValuationTab,
  },
  directives: { 'b-tooltip': VBTooltip },
  data() {
    return {
      activeTab: 'hotel',
      pageLoading: true,
      loadError: '',
      showOthersSummary: true,
      activeOtherLog: {},
      summaryCounts: { ...EMPTY_COUNTS },
      selectedOtherKey: '',
      selectedOtherCountType: '',
      detailsLoading: false,
      detailColumns: [],
      detailRows: [],
      classifiedRoomsLoading: true,
      classifiedRoomsError: '',
      roomVariants: [],
      expandedVariant: '',
      roomSummariesLoading: false,
      roomSummariesError: '',
      roomSummaries: [],
      roomSummaryRequestId: 0,
      expandedRoomSummary: '',
      variantRoomsLoading: false,
      variantRoomsError: '',
      variantRooms: [],
      roomOptions: [],
      variantRequestId: 0,
      variantPagination: {
        page: 1,
        per_page: 20,
        total: 0,
        total_pages: 1,
      },
      selectedLanguage: 'en',
      availableLanguages: ['en', 'ar', 'es', 'tr'],
      translationMappings: [],
      valuationSampleIndex: Math.floor(Math.random() * valuationSamples.length),
    }
  },
  computed: {
    ssid() {
      return String(this.$route.query.ssid || '').trim()
    },
    groupId() {
      return String(this.$route.query.group_id || '').trim()
    },
    hotelSupplier() {
      const log = this.activeOtherLog || {}
      return String(log.supplier || log.suppliers || this.$route.query.supplier || '').trim()
    },
    otherRows() {
      const current = this.activeOtherLog || {}
      const createRow = (key, label) => ({
        key,
        label,
        count: this.toCount(this.summaryCounts[key]),
        displayCount: this.formatOtherCount(this.summaryCounts[key]),
        originalCount: this.toCount(current[key]),
        originalDisplayCount: this.formatOtherCount(current[key]),
      })
      return [
        createRow('room_summary_others', 'Room Summary Others'),
        createRow('bed_type_others', 'Bed Type Others'),
        createRow('meals_others', 'Meals Others'),
        createRow('view_others', 'View Others'),
      ]
    },
    selectedOtherSummary() {
      return this.otherRows.find(item => item.key === this.selectedOtherKey) || { label: '-' }
    },
    selectedOtherCountTypeLabel() {
      return this.selectedOtherCountType === 'others' ? 'Others Count' : 'Invalid Others Count'
    },
    roomSummaryOthersDisplay() {
      return this.formatOtherCount(this.activeOtherLog.room_summary_others)
    },
    totalClassifiedRooms() {
      return this.roomVariants.reduce((total, variant) => total + this.toCount(variant.room_summary_count), 0)
    },
    variantPageStart() {
      if (!this.variantPagination.total) return 0
      return ((this.variantPagination.page - 1) * this.variantPagination.per_page) + 1
    },
    variantPageEnd() {
      return Math.min(
        this.variantPagination.page * this.variantPagination.per_page,
        this.variantPagination.total,
      )
    },
  },
  mounted() {
    this.loadPage()
  },
  methods: {
    async loadPage() {
      this.pageLoading = true
      this.loadError = ''
      this.resetDetails()
      this.summaryCounts = { ...EMPTY_COUNTS }

      if (!this.ssid || !this.groupId) {
        this.loadError = 'The selected log reference is missing. Please return to Client Logs and open it again.'
        this.pageLoading = false
        return
      }

      try {
        this.activeOtherLog = getRoomMappingOthersContext(this.ssid, this.groupId) || await this.fetchLog()
        if (!this.activeOtherLog) {
          throw new Error('The selected log was not found.')
        }
        const [{ data }] = await Promise.all([
          axios.post('room_mapping_client_log_others', {
            ssid: this.ssid,
            group_id: this.groupId,
          }),
          this.loadRoomVariants(),
          this.loadTranslations(),
        ])
        this.summaryCounts = { ...EMPTY_COUNTS, ...(data.summary || {}) }
      } catch (error) {
        this.loadError = this.getErrorMessage(error)
      } finally {
        this.pageLoading = false
      }
    },
    async fetchLog() {
      const { data } = await axios.post('room_mapping_client_logs', {
        page: 1,
        per_page: 20,
        ssid: this.ssid,
        group_id: this.groupId,
        sort_by: 'create_date',
        sort_order: 'desc',
      })
      const logs = data.logs || []
      return logs.find(row => String(row.ssid) === this.ssid && String(row.group_id) === this.groupId) || null
    },
    async loadTranslations() {
      try {
        const { data } = await axios.get('room_mapping_translations')
        const languages = Array.isArray(data.languages) && data.languages.length ? data.languages : ['en']
        this.mergeAvailableLanguages(languages)
        this.translationMappings = Array.isArray(data.mappings) ? data.mappings : []
        if (!this.availableLanguages.includes(this.selectedLanguage)) this.selectedLanguage = 'en'
      } catch (error) {
        this.translationMappings = []
        this.mergeAvailableLanguages(['en'])
      }
    },
    languageLabel(language) {
      const labels = { en: 'English', ar: 'Arabic', es: 'Spanish', tr: 'Turkish' }
      return labels[language] || String(language || '').toUpperCase()
    },
    mergeAvailableLanguages(languages) {
      const normalized = (Array.isArray(languages) ? languages : [])
        .map(language => String(language || '').trim().toLowerCase())
        .filter(Boolean)
      this.availableLanguages = [...new Set(['en', ...this.availableLanguages, ...normalized])]
        .sort((left, right) => {
          if (left === 'en') return -1
          if (right === 'en') return 1
          return left.localeCompare(right)
        })
      if (!this.availableLanguages.includes(this.selectedLanguage)) this.selectedLanguage = 'en'
    },
    translationObject(value) {
      if (value && typeof value === 'object' && !Array.isArray(value)) return value
      if (typeof value !== 'string' || !value.trim()) return {}
      try {
        const parsed = JSON.parse(value)
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
      } catch (error) {
        return {}
      }
    },
    translatedRoomField(row, field, sourceValue, fallbackCategories = null) {
      const source = String(sourceValue == null ? '' : sourceValue)
      if (!source || this.selectedLanguage === 'en') return source
      const translations = this.translationObject(row && row.translations)
      const languageValues = this.translationObject(translations[this.selectedLanguage])
      const translated = languageValues[field]
      if (translated !== null && translated !== undefined && String(translated).trim()) {
        return String(translated)
      }
      return this.translateValue(source, fallbackCategories)
    },
    translatedRoomType(room) {
      const source = String(room && room.room_type != null ? room.room_type : '')
      if (!source || this.selectedLanguage === 'en') return source
      const translations = this.translationObject(room && room.room_type_translation)
      const translated = translations[this.selectedLanguage]
      if (translated !== null && translated !== undefined && String(translated).trim()) {
        return String(translated)
      }
      return this.translateValue(source)
    },
    translateValue(value, categories = null) {
      const source = String(value == null ? '' : value)
      if (!source || this.selectedLanguage === 'en') return source
      const categorySet = categories ? new Set(categories) : null
      const candidates = this.translationMappings
        .filter(mapping => !categorySet || categorySet.has(mapping.category))
        .filter(mapping => mapping.translations && mapping.translations[this.selectedLanguage])
        .sort((left, right) => String(right.standard_value).length - String(left.standard_value).length)

      const exact = candidates.find(mapping => String(mapping.standard_value).toLocaleLowerCase() === source.toLocaleLowerCase())
      if (exact) return exact.translations[this.selectedLanguage]

      return candidates.reduce((translated, mapping) => {
        const standardValue = String(mapping.standard_value || '')
        if (!standardValue) return translated
        const escaped = standardValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        return translated.replace(new RegExp(`\\b${escaped}\\b`, 'gi'), mapping.translations[this.selectedLanguage])
      }, source)
    },
    async loadRoomVariants() {
      this.classifiedRoomsLoading = true
      this.classifiedRoomsError = ''
      try {
        const { data } = await axios.post('room_mapping_classified_rooms', {
          ssid: this.ssid,
          group_id: this.groupId,
        })
        this.roomVariants = this.sortRoomVariants(data.variants || [])
        this.mergeAvailableLanguages(data.available_languages)
        if (!this.roomVariants.some(item => item.room_variant === this.expandedVariant)) {
          this.resetVariantDetails()
        }
      } catch (error) {
        this.roomVariants = []
        this.classifiedRoomsError = this.getErrorMessage(error)
      } finally {
        this.classifiedRoomsLoading = false
      }
    },
    toggleRoomVariant(variant) {
      if (!variant || !variant.room_variant) return
      if (this.expandedVariant === variant.room_variant) {
        this.resetVariantDetails()
        return
      }
      this.resetVariantDetails()
      this.expandedVariant = variant.room_variant
      this.loadRoomSummaries()
    },
    sortRoomVariants(variants) {
      return [...variants].sort((left, right) => {
        const leftIsOthers = String(left && left.room_variant || '').trim().toLowerCase() === 'others'
        const rightIsOthers = String(right && right.room_variant || '').trim().toLowerCase() === 'others'
        if (leftIsOthers === rightIsOthers) return 0
        return leftIsOthers ? 1 : -1
      })
    },
    async loadRoomSummaries() {
      if (!this.expandedVariant) return
      const requestedVariant = this.expandedVariant
      const requestId = this.roomSummaryRequestId + 1
      this.roomSummaryRequestId = requestId
      this.roomSummariesLoading = true
      this.roomSummariesError = ''
      this.roomSummaries = []
      try {
        const { data } = await axios.post('room_mapping_classified_rooms', {
          ssid: this.ssid,
          group_id: this.groupId,
          room_variant: requestedVariant,
        })
        if (requestId !== this.roomSummaryRequestId || requestedVariant !== this.expandedVariant) return
        this.roomSummaries = data.room_summaries || []
        this.mergeAvailableLanguages(data.available_languages)
      } catch (error) {
        if (requestId !== this.roomSummaryRequestId || requestedVariant !== this.expandedVariant) return
        this.roomSummariesError = this.getErrorMessage(error)
      } finally {
        if (requestId === this.roomSummaryRequestId) {
          this.roomSummariesLoading = false
        }
      }
    },
    toggleRoomSummary(summary) {
      if (!summary || !summary.room_summary) return
      if (this.expandedRoomSummary === summary.room_summary) {
        this.resetRoomDetails()
        return
      }
      this.resetRoomDetails()
      this.expandedRoomSummary = summary.room_summary
      this.loadVariantRooms()
    },
    async loadVariantRooms() {
      if (!this.expandedVariant || !this.expandedRoomSummary) return
      const requestedVariant = this.expandedVariant
      const requestedSummary = this.expandedRoomSummary
      const requestId = this.variantRequestId + 1
      this.variantRequestId = requestId
      this.variantRoomsLoading = true
      this.variantRoomsError = ''
      this.variantRooms = []
      try {
        const { data } = await axios.post('room_mapping_classified_rooms', {
          ssid: this.ssid,
          group_id: this.groupId,
          room_variant: requestedVariant,
          room_summary: requestedSummary,
          page: this.variantPagination.page,
          per_page: this.variantPagination.per_page,
        })
        if (
          requestId !== this.variantRequestId
          || requestedVariant !== this.expandedVariant
          || requestedSummary !== this.expandedRoomSummary
        ) return
        this.variantRooms = data.rooms || []
        this.roomOptions = Array.isArray(data.options) && data.options.length
          ? data.options
          : this.fallbackRoomOptions(this.variantRooms)
        this.mergeAvailableLanguages(data.available_languages)
        this.variantPagination = {
          ...this.variantPagination,
          ...(data.pagination || {}),
        }
      } catch (error) {
        if (
          requestId !== this.variantRequestId
          || requestedVariant !== this.expandedVariant
          || requestedSummary !== this.expandedRoomSummary
        ) return
        this.variantRoomsError = this.getErrorMessage(error)
        this.variantRooms = []
        this.roomOptions = []
      } finally {
        if (requestId === this.variantRequestId) {
          this.variantRoomsLoading = false
        }
      }
    },
    changeVariantPage(page) {
      if (
        page < 1
        || page > this.variantPagination.total_pages
        || page === this.variantPagination.page
      ) return
      this.variantPagination.page = page
      this.loadVariantRooms()
    },
    resetVariantDetails() {
      this.roomSummaryRequestId += 1
      this.expandedVariant = ''
      this.roomSummariesLoading = false
      this.roomSummariesError = ''
      this.roomSummaries = []
      this.resetRoomDetails()
    },
    resetRoomDetails() {
      this.variantRequestId += 1
      this.expandedRoomSummary = ''
      this.variantRoomsLoading = false
      this.variantRoomsError = ''
      this.variantRooms = []
      this.roomOptions = []
      this.variantPagination = {
        page: 1,
        per_page: 20,
        total: 0,
        total_pages: 1,
      }
    },
    async toggleOtherDetails(item, countType = 'invalid') {
      const safeCountType = countType === 'others' ? 'others' : 'invalid'
      const availableCount = safeCountType === 'others' ? item && item.originalCount : item && item.count
      if (!item || availableCount <= 0) return

      if (this.isSelectedOtherDetail(item, safeCountType) && this.detailRows.length) {
        this.resetDetails()
        return
      }

      this.selectedOtherKey = item.key
      this.selectedOtherCountType = safeCountType
      this.detailsLoading = true
      this.detailColumns = []
      this.detailRows = []
      try {
        const { data } = await axios.post('room_mapping_client_log_others', {
          ssid: this.ssid,
          group_id: this.groupId,
          detail_type: item.key,
          count_type: safeCountType,
        })
        const detail = data.detail || {}
        this.summaryCounts = { ...this.summaryCounts, ...(data.summary || {}) }
        this.detailColumns = detail.columns || []
        this.detailRows = detail.rows || []
      } catch (error) {
        this.detailColumns = []
        this.detailRows = []
      } finally {
        this.detailsLoading = false
      }
    },
    resetDetails() {
      this.selectedOtherKey = ''
      this.selectedOtherCountType = ''
      this.detailsLoading = false
      this.detailColumns = []
      this.detailRows = []
    },
    isSelectedOtherDetail(item, countType) {
      return Boolean(item && this.selectedOtherKey === item.key && this.selectedOtherCountType === countType)
    },
    goBack() {
      const returnTo = String(this.$route.query.return_to || '')
      const safePrefix = this.$router.resolve({ name: 'roomMappingClientLogs' }).href
      if (returnTo === safePrefix || returnTo.startsWith(`${safePrefix}?`)) {
        this.$router.replace(returnTo)
        return
      }
      this.$router.push({ name: 'roomMappingClientLogs' })
    },
    getErrorMessage(error) {
      const apiMessage = error && error.response && error.response.data && error.response.data.message
      return apiMessage || (error && error.message) || 'Please try again.'
    },
    formatCell(value) {
      return value === null || value === undefined || value === '' ? '-' : value
    },
    formatClientCell(row) {
      const client = this.formatCell(row && row.client)
      if (client === '-') return '-'
      return row && row.client_type ? `${client} (${row.client_type})` : client
    },
    formatOtherCount(value) {
      return value === null || value === undefined || value === '' ? '0' : `${value}`
    },
    toCount(value) {
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : 0
    },
    hasRoomValue(value) {
      return value !== null && value !== undefined && value !== ''
    },
    formatPolicy(value) {
      if (!this.hasRoomValue(value)) return '-'
      if (Number(value) === 0) return 'Non Refundable'
      if (Number(value) === 1) return 'Refundable'
      return value
    },
    formatRoomRate(room) {
      const displayRate = room && room.display_room_rate
      if (!this.hasRoomValue(displayRate)) return '-'
      const rateTranslations = this.translationObject(room && room.rate_translations)
      const storedTranslation = this.selectedLanguage === 'en' ? '' : rateTranslations[this.selectedLanguage]
      const translatedRate = this.hasRoomValue(storedTranslation) ? storedTranslation : displayRate
      const numericRate = Number(translatedRate)
      const formattedRate = Number.isFinite(numericRate) && !this.hasRoomValue(storedTranslation)
        ? numericRate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : translatedRate
      return `${room.display_currency_code || ''} ${formattedRate}`.trim()
    },
    summaryOptionCount(summary) {
      return this.hasRoomValue(summary && summary.option_count)
        ? this.toCount(summary.option_count)
        : this.toCount(summary && summary.room_count)
    },
    optionKey(option) {
      return [option.supplier_id, option.local_hotel_id, option.rate_info_cntr].join('-')
    },
    optionNumber(index) {
      const absoluteIndex = ((this.variantPagination.page - 1) * this.variantPagination.per_page) + index + 1
      return String(absoluteIndex).padStart(2, '0')
    },
    optionSupplier(option) {
      const values = [option.supplier_id, option.local_hotel_id].filter(value => this.hasRoomValue(value))
      return values.length ? values.join(' · ') : 'Supplier reference unavailable'
    },
    roomDisplayName(room) {
      const details = room && room.room_rate_details || {}
      const summary = room.room_summary || details.room_group_summary
      if (summary) return this.translatedRoomField(room, 'room_summary', summary, ['room'])
      return this.translatedRoomType(room) || '-'
    },
    roomOccupancy(room) {
      const details = room && room.room_rate_details || {}
      const adults = this.toCount(details.number_of_adults)
      const children = this.toCount(details.number_of_child)
      const parts = []
      let supplierRoom = String(details.room_category || details.room_type || '').trim()
      const roomName = String(details.room_name || '').trim()
      if (roomName && supplierRoom.toLowerCase().startsWith(roomName.toLowerCase())) {
        supplierRoom = supplierRoom.slice(roomName.length).trim()
      }
      if (supplierRoom) parts.push(supplierRoom)
      else if (roomName) parts.push(roomName.replace(/_/g, ' '))
      if (adults) parts.push(`${adults} ${this.pluralize(adults, 'adult', 'adults')}`)
      if (children) parts.push(`${children} ${this.pluralize(children, 'child', 'children')}`)
      return parts.join(' · ') || `Room ${this.toCount(room.room_rates_cntr) + 1}`
    },
    roomBedType(room) {
      const details = room && room.room_rate_details || {}
      const value = room.bed_type
        || details.bed_type
        || details.room_group_bed_type
        || details.room_group_bed
      return this.translatedRoomField(room, 'bed_type', value, ['bed']) || '-'
    },
    roomView(room) {
      const details = room && room.room_rate_details || {}
      const value = room.view || details.view || details.room_group_view
      return this.translatedRoomField(room, 'view', value, ['view']) || '-'
    },
    isRefundable(value) {
      return Number(value) === 1
    },
    formatIndividualRoomRate(room, option) {
      const details = room && room.room_rate_details || {}
      const value = details.displayRoomTypeRates
      if (!this.hasRoomValue(value)) return 'Included'
      const numericRate = Number(value)
      const formatted = Number.isFinite(numericRate)
        ? numericRate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : value
      return `${option.display_currency_code || room.display_currency_code || ''} ${formatted}`.trim()
    },
    formatOptionRate(option) {
      return this.formatRoomRate({
        display_room_rate: option.display_room_rate,
        display_currency_code: option.display_currency_code,
      })
    },
    fallbackRoomOptions(rooms) {
      return (rooms || []).map((room, index) => ({
        supplier_id: room.supplier_id || '',
        local_hotel_id: room.local_hotel_id || '',
        rate_info_cntr: this.hasRoomValue(room.rate_info_cntr) ? room.rate_info_cntr : index,
        display_room_rate: room.display_room_rate,
        display_currency_code: room.display_currency_code,
        rooms: [room],
      }))
    },
    formatDetailColumn(value) {
      return this.formatCell(value).replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
    },
    pluralize(value, singular, plural) {
      return this.toCount(value) === 1 ? singular : plural
    },
  },
}
</script>

<style scoped>
.others-page {
  min-height: calc(100vh - 40px);
  padding: 20px;
  text-align: left;
  background: linear-gradient(180deg, #f3f7fb 0%, #f9fbfd 100%);
}
.page-toolbar {
  max-width: 1500px;
  margin: 0 auto 14px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.back-btn,
.regenerate-yaml-btn,
.manage-mapping-btn {
  min-height: 40px;
  padding: 0 14px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
}
.back-btn {
  border: 1px solid #ccdbea;
  color: #315473;
  background: #fff;
}
.regenerate-yaml-btn {
  border: 1px solid #6d28d9;
  color: #6d28d9;
  background: #fff;
}
.manage-mapping-btn {
  border: 1px solid #6d28d9;
  color: #fff;
  background: #6d28d9;
  box-shadow: 0 7px 16px rgba(109, 40, 217, 0.2);
}
.back-btn:hover { background: #f5f9fd; }
.regenerate-yaml-btn:hover { background: #f5efff; }
.manage-mapping-btn:hover { background: #5b21b6; }
.regeneration-content > p {
  margin: 0 0 16px;
  color: #625b70;
  font-size: 13px;
  line-height: 1.55;
}
.regeneration-message {
  margin-top: 12px;
  padding: 11px 12px;
  border-radius: 9px;
  font-size: 12px;
  line-height: 1.5;
}
.regeneration-message.error {
  color: #a1263d;
  border: 1px solid #f2bdc8;
  background: #fff1f4;
}
.regeneration-message.success {
  color: #087443;
  border: 1px solid #a9e2c5;
  background: #edfff5;
}
.regeneration-actions {
  margin-top: 18px;
  display: flex;
  justify-content: flex-end;
  gap: 9px;
}
.regeneration-actions button {
  min-height: 39px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
}
.cancel-regeneration-btn {
  border: 1px solid #d9d1e5;
  color: #5f5570;
  background: #fff;
}
.confirm-regeneration-btn {
  border: 1px solid #6d28d9;
  color: #fff;
  background: #6d28d9;
}
.regeneration-actions button:disabled {
  cursor: not-allowed;
  opacity: .6;
}
.others-page-card {
  max-width: 1500px;
  min-height: 420px;
  margin: 0 auto;
  padding: 22px;
  border: 1px solid #dce8f4;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(17, 33, 58, 0.1);
}
.others-layout { display: flex; flex-direction: column; gap: 18px; }
.others-header {
  padding-bottom: 16px;
  border-bottom: 1px solid #e4edf6;
}
.others-header h2 { margin: 0; color: #11213a; font-size: 26px; }
.others-header p { margin: 6px 0 0; color: #5f738f; font-size: 13px; overflow-wrap: anywhere; }
.others-header p span { padding: 0 4px; }
.summary-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}
.summary-chip {
  padding: 12px 14px;
  border: 1px solid #dce8f4;
  border-radius: 10px;
  background: #fff;
}
.summary-chip span {
  display: block;
  margin-bottom: 6px;
  color: #6f86a3;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}
.summary-chip strong { display: block; color: #11213a; font-size: 14px; overflow-wrap: anywhere; }
.collapse-card,
.detail-wrap,
.detail-loading {
  overflow: hidden;
  border: 1px solid #dce8f4;
  border-radius: 12px;
  background: #fff;
}
.collapse-strip {
  width: 100%;
  min-height: 58px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #e4edf6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  color: #11213a;
  background: linear-gradient(135deg, #f7fbff 0%, #fff 54%, #eef6ff 100%);
}
.collapse-title,
.collapse-meta { display: inline-flex; align-items: center; gap: 10px; }
.collapse-title { font-size: 15px; font-weight: 800; }
.collapse-title .b-icon { color: #1673d5; font-size: 18px; }
.collapse-meta { justify-content: flex-end; flex-wrap: wrap; color: #5f738f; font-size: 12px; font-weight: 700; }
.collapse-meta strong {
  min-width: 42px;
  padding: 6px 12px;
  border-radius: 999px;
  color: #fff;
  background: #123f72;
}
.summary-table-wrap { overflow: hidden; }
.table-topbar {
  padding: 13px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.table-topbar h5,
.detail-topbar h5 { margin: 0; color: #11213a; font-size: 15px; font-weight: 700; }
.table-topbar p,
.detail-topbar p { margin: 4px 0 0; color: #5f738f; font-size: 12px; }
.mini-badge {
  min-width: 72px;
  padding: 5px 10px;
  border-radius: 999px;
  text-align: center;
  color: #123f72;
  background: #eef3f8;
  font-size: 12px;
  font-weight: 700;
}
.table-scroll,
.detail-scroll { overflow: auto; }
.summary-table,
.detail-table { width: 100%; min-width: 720px; border-collapse: separate; border-spacing: 0; }
.summary-table th {
  padding: 10px 14px;
  color: #fff;
  background: #123f72;
  font-size: 12px;
  text-align: left;
}
.summary-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #e8eef5;
  font-size: 13px;
}
.summary-table tbody tr:nth-child(even) { background: #f8fbff; }
.summary-table tbody tr.active,
.summary-table tbody tr:hover { background: #eef5ff; }
.type-cell { display: flex; align-items: center; gap: 10px; color: #1e2948; font-weight: 600; }
.type-cell span { width: 9px; height: 9px; border-radius: 50%; background: #1673d5; }
.count-btn {
  min-width: 78px;
  min-height: 32px;
  padding: 6px 13px;
  border: 1px solid #cdddf0;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 800;
  background: #fff;
}
.count-btn.saved { color: #315473; background: #f8fbff; }
.count-btn.invalid { color: #925a12; border-color: #f5d9b4; background: #fff8f0; }
.count-btn.saved.active,
.count-btn.saved:hover:not(:disabled) { color: #fff; border-color: #123f72; background: #123f72; }
.count-btn.invalid.active,
.count-btn.invalid:hover:not(:disabled) { color: #fff; border-color: #1673d5; background: #1673d5; }
.count-btn:disabled { cursor: not-allowed; opacity: .55; }
.detail-topbar { padding: 14px; border-bottom: 1px solid #e8eef5; }
.detail-table th {
  padding: 10px 12px;
  border-bottom: 1px solid #dce8f4;
  color: #123f72;
  background: #f5f9fd;
  font-size: 12px;
  white-space: nowrap;
  text-align: left;
}
.detail-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e8eef5;
  color: #11213a;
  font-size: 13px;
  vertical-align: top;
  word-break: break-word;
}
.detail-table tbody tr:nth-child(even) { background: #fbfdff; }
.detail-loading { padding: 14px; }
.classified-rooms-section {
  overflow: hidden;
  border: 1px solid #d7e5f4;
  border-radius: 14px;
  background: #f8fbff;
  box-shadow: 0 14px 34px rgba(36, 79, 124, .08);
}
.classified-section-header {
  padding: 20px;
  border-bottom: 1px solid #dce8f4;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  background:
    radial-gradient(circle at 88% 20%, rgba(79, 70, 229, .12), transparent 25%),
    linear-gradient(120deg, #fff 0%, #f5f9ff 58%, #eef3ff 100%);
}
.classified-heading {
  display: flex;
  align-items: center;
  gap: 14px;
}
.classified-heading-icon {
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  border-radius: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #3858e9, #5c36da);
  box-shadow: 0 10px 22px rgba(69, 75, 217, .28);
  font-size: 20px;
}
.section-eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #60779a;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .12em;
  text-transform: uppercase;
}
.classified-heading h3 {
  margin: 0;
  color: #10213e;
  font-size: 20px;
  font-weight: 800;
}
.classified-heading p {
  margin: 4px 0 0;
  color: #647693;
  font-size: 12px;
}
.classified-stats {
  display: flex;
  align-items: center;
  gap: 8px;
}
.classified-stats span {
  min-width: 92px;
  padding: 8px 12px;
  border: 1px solid #d6e2f3;
  border-radius: 10px;
  color: #5f718d;
  background: rgba(255, 255, 255, .88);
  font-size: 11px;
  text-align: center;
}
.classified-stats strong {
  display: block;
  color: #173567;
  font-size: 17px;
  line-height: 1.1;
}
.variant-list {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 9px;
}
.variant-card {
  overflow: hidden;
  border: 1px solid #d9e4f3;
  border-radius: 11px;
  background: #fff;
  box-shadow: 0 5px 14px rgba(29, 58, 104, .06);
  transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease;
}
.variant-card:hover {
  border-color: #bdcfee;
  transform: translateY(-1px);
  box-shadow: 0 9px 20px rgba(29, 58, 104, .1);
}
.variant-card.expanded {
  border-color: #8098ef;
  box-shadow: 0 12px 28px rgba(55, 73, 190, .14);
}
.variant-trigger {
  width: 100%;
  min-height: 54px;
  padding: 10px 14px;
  border: 0;
  display: grid;
  grid-template-columns: 32px minmax(200px, 1fr) auto auto 20px;
  align-items: center;
  gap: 12px;
  color: #fff;
  text-align: left;
  background: linear-gradient(100deg, #315be8 0%, #3e62ee 54%, #4d45df 100%);
}
.variant-card.expanded .variant-trigger {
  background: linear-gradient(100deg, #243fb4 0%, #3158dd 54%, #4937c7 100%);
}
.variant-toggle-icon {
  width: 25px;
  height: 25px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #3155da;
  background: #fff;
  font-size: 16px;
  font-weight: 900;
}
.variant-name {
  overflow: hidden;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: .01em;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.variant-mapping-progress {
  padding: 5px 9px;
  border: 1px solid rgba(255, 255, 255, .28);
  border-radius: 999px;
  color: rgba(255, 255, 255, .92);
  background: rgba(255, 255, 255, .1);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.variant-count {
  min-width: 84px;
  padding: 6px 11px;
  border-radius: 999px;
  color: #203ca8;
  background: #fff;
  font-size: 11px;
  font-weight: 800;
  text-align: center;
  white-space: nowrap;
}
.variant-chevron { justify-self: end; }
.variant-panel {
  border-top: 1px solid #dce6f5;
  background: #fff;
}
.variant-panel-topbar {
  padding: 12px 15px;
  border-bottom: 1px solid #e4ecf6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #f8faff;
}
.variant-panel-topbar > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.variant-panel-topbar strong {
  color: #132b54;
  font-size: 13px;
}
.variant-panel-topbar span {
  color: #6a7c97;
  font-size: 11px;
}
.classification-key {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.classification-key i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4d49dc;
}
.classified-table-scroll {
  overflow: auto;
  max-height: 560px;
}
.classified-table {
  width: 100%;
  min-width: 1260px;
  border-collapse: separate;
  border-spacing: 0;
}
.classified-table thead th {
  position: sticky;
  z-index: 1;
  top: 0;
  padding: 10px 12px;
  border-bottom: 1px solid #cbd9ed;
  color: #30476d;
  background: #edf3fb;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .06em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}
.classified-table tbody td {
  max-width: 310px;
  padding: 12px;
  border-bottom: 1px solid #e6edf6;
  color: #263752;
  font-size: 12px;
  vertical-align: top;
}
.classified-table tbody tr:nth-child(even) { background: #fbfdff; }
.classified-table tbody tr:hover { background: #f1f6ff; }
.room-primary {
  overflow: hidden;
  color: #172a49;
  font-weight: 750;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.room-secondary {
  overflow: hidden;
  margin-top: 4px;
  color: #71819a;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.room-type { max-width: 270px; }
.room-category { max-width: 270px; }
.variant-result-pill {
  display: inline-flex;
  padding: 5px 9px;
  border: 1px solid #ccd8ff;
  border-radius: 8px;
  color: #3348b8;
  background: #f1f4ff;
  font-weight: 750;
}
.classification-result strong {
  display: block;
  color: #152b53;
  font-size: 12px;
}
.classification-tags {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.classification-tags span {
  padding: 3px 6px;
  border-radius: 5px;
  color: #50657f;
  background: #eef3f8;
  font-size: 9px;
  white-space: nowrap;
}
.mapping-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 8px;
  border: 1px solid #f1d59a;
  border-radius: 999px;
  color: #91610b;
  background: #fff8e9;
  font-size: 10px;
  font-weight: 800;
  white-space: nowrap;
}
.mapping-status.mapped {
  border-color: #b9e4cd;
  color: #167044;
  background: #eaf8f0;
}
.room-rate {
  color: #173e79;
  font-weight: 800;
  white-space: nowrap;
}
.updated-at {
  color: #657791;
  font-size: 10px;
  white-space: nowrap;
}
.variant-trigger {
  grid-template-columns: 32px minmax(0, 1fr) 20px;
}
.variant-name strong,
.room-summary-trigger strong {
  font-weight: 800;
}
.room-summary-list {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f4f7fc;
}
.room-summary-card {
  overflow: hidden;
  border: 1px solid #d9e3f3;
  border-radius: 9px;
  background: #fff;
}
.room-summary-card.expanded {
  border-color: #7f98ef;
  box-shadow: 0 7px 18px rgba(51, 76, 181, .12);
}
.room-summary-trigger {
  width: 100%;
  min-height: 47px;
  padding: 8px 13px;
  border: 0;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 10px;
  color: #fff;
  text-align: left;
  background: linear-gradient(100deg, #4569ed 0%, #3460ec 56%, #4546dd 100%);
}
.room-summary-trigger > span:nth-child(2) {
  overflow: hidden;
  font-size: 13px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.summary-toggle-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #3e5fe2;
  background: #fff;
}
.room-summary-panel {
  border-top: 1px solid #dce5f3;
  background: #f4f4f4;
}
.classified-table-scroll {
  max-height: 560px;
  overflow: auto;
}
.classified-table {
  width: 100%;
  min-width: 720px;
  border-collapse: separate;
  border-spacing: 0;
}
.classified-table thead th {
  position: sticky;
  z-index: 1;
  top: 0;
  padding: 11px 14px;
  border-bottom: 1px solid #3658e9;
  color: #2f47dc;
  background: #f1f2f5;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  text-align: left;
  text-transform: none;
}
.classified-table thead th:first-child { width: 42%; }
.classified-table thead th:nth-child(2) { width: 18%; }
.classified-table thead th:nth-child(3) { width: 18%; }
.classified-table thead th:nth-child(4) { width: 22%; }
.classified-table tbody td {
  max-width: none;
  padding: 14px;
  border-bottom: 1px solid #d1d1d1;
  color: #6b6b6b;
  background: #f4f4f4;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
  vertical-align: top;
}
.classified-table tbody tr:nth-child(even),
.classified-table tbody tr:hover {
  background: transparent;
}
.classified-table tbody td:last-child strong {
  color: #3048ed;
  white-space: nowrap;
}
.variant-pagination {
  padding: 11px 14px;
  border-top: 1px solid #e3ebf5;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  background: #fbfdff;
}
.variant-pagination span {
  color: #5b6f8c;
  font-size: 11px;
  font-weight: 700;
}
.variant-pagination button,
.variant-panel-state button,
.classified-state button {
  min-height: 31px;
  padding: 0 10px;
  border: 1px solid #c9d7ea;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #294c78;
  background: #fff;
  font-size: 10px;
  font-weight: 750;
}
.variant-pagination button:disabled {
  cursor: not-allowed;
  opacity: .5;
}
.classified-state,
.variant-panel-state {
  min-height: 94px;
  padding: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #5d708d;
  text-align: left;
}
.classified-state > .b-icon,
.variant-panel-state > .b-icon {
  color: #4a61d8;
  font-size: 23px;
}
.classified-state strong { display: block; color: #1f365c; }
.classified-state p { margin: 3px 0 0; font-size: 11px; }
.classified-state.error > div { flex: 1; }
.variant-skeleton,
.room-table-skeleton {
  padding: 14px;
}
.variant-skeleton div,
.room-table-skeleton div {
  height: 48px;
  margin-bottom: 9px;
  border-radius: 9px;
  background: linear-gradient(90deg, #edf2f9 25%, #f8faff 50%, #edf2f9 75%);
  background-size: 200% 100%;
  animation: loading-wave 1.4s infinite;
}
.variant-skeleton div:last-child,
.room-table-skeleton div:last-child { margin-bottom: 0; }
.detail-loading div,
.others-skeleton-row,
.others-skeleton-card {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  background: #eef4fb;
}
.detail-loading div { height: 42px; margin-bottom: 10px; }
.detail-loading div:last-child { margin-bottom: 0; }
.others-skeleton { width: 100%; }
.others-skeleton-card { height: 110px; margin-bottom: 18px; }
.others-skeleton-row { height: 58px; margin-bottom: 14px; }
.others-skeleton-row::after,
.others-skeleton-card::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, .75), transparent);
  animation: shimmer 1.3s infinite;
}
.empty-state,
.page-error {
  min-height: 260px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: #4b607d;
}
.empty-state .b-icon,
.page-error .b-icon { margin-bottom: 12px; color: #1673d5; font-size: 36px; }
.empty-state h5,
.page-error h4 { margin: 0 0 6px; color: #11213a; }
.empty-state p,
.page-error p { margin: 0; }
.page-error button {
  margin-top: 16px;
  padding: 8px 14px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: #1673d5;
}

/* Premium hospitality theme */
.others-page {
  --ink: #1d2420;
  --ink-soft: #39423d;
  --muted: #747b76;
  --gold: #a77b36;
  --gold-deep: #815b24;
  --gold-pale: #f4ead7;
  --ivory: #fbfaf6;
  --canvas: #f1efe9;
  --line: #dedbd2;
  min-height: calc(100vh - 40px);
  padding: 28px clamp(16px, 2.2vw, 36px) 44px;
  background:
    radial-gradient(circle at 8% 0%, rgba(184, 145, 78, .12), transparent 26rem),
    radial-gradient(circle at 92% 18%, rgba(63, 78, 68, .08), transparent 30rem),
    linear-gradient(145deg, #f7f5ef 0%, #eeece6 100%);
}
.page-toolbar {
  max-width: 1480px;
  margin-bottom: 16px;
}
.back-btn,
.regenerate-yaml-btn,
.manage-mapping-btn {
  min-height: 42px;
  padding: 0 17px;
  border-radius: 11px;
  font-size: 12px;
  letter-spacing: .015em;
  transition: transform .2s ease, box-shadow .2s ease, background .2s ease;
}
.back-btn {
  border-color: rgba(42, 51, 46, .16);
  color: var(--ink-soft);
  background: rgba(255, 255, 255, .72);
  box-shadow: 0 4px 16px rgba(35, 42, 38, .04);
  backdrop-filter: blur(10px);
}
.back-btn:hover {
  color: var(--ink);
  background: #fff;
  transform: translateX(-2px);
}
.manage-mapping-btn {
  border-color: #202722;
  color: #fff;
  background: #202722;
  box-shadow: 0 9px 22px rgba(29, 36, 32, .16);
}
.manage-mapping-btn:hover {
  border-color: #2f3933;
  background: #2f3933;
  box-shadow: 0 12px 26px rgba(29, 36, 32, .22);
  transform: translateY(-1px);
}
.others-page-card {
  max-width: 1480px;
  padding: clamp(18px, 2.2vw, 32px);
  border: 1px solid rgba(87, 81, 68, .13);
  border-radius: 22px;
  background: rgba(255, 255, 252, .94);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, .9) inset,
    0 22px 60px rgba(49, 45, 37, .11);
}
.others-layout { gap: 22px; }
.others-header {
  position: relative;
  min-height: 108px;
  padding: 24px 26px;
  border: 0;
  border-radius: 17px;
  display: flex;
  align-items: center;
  gap: 17px;
  overflow: hidden;
  color: #fff;
  background:
    radial-gradient(circle at 88% 15%, rgba(191, 153, 88, .2), transparent 26%),
    linear-gradient(125deg, #19201c 0%, #29332d 58%, #38463d 100%);
  box-shadow: 0 14px 32px rgba(25, 32, 28, .17);
}
.others-header::after {
  content: '';
  position: absolute;
  right: -45px;
  bottom: -95px;
  width: 240px;
  height: 240px;
  border: 1px solid rgba(218, 190, 139, .18);
  border-radius: 50%;
  box-shadow: 0 0 0 28px rgba(218, 190, 139, .045), 0 0 0 58px rgba(218, 190, 139, .025);
}
.header-monogram {
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  border: 1px solid rgba(232, 205, 153, .36);
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #e4c98f;
  background: rgba(255, 255, 255, .07);
  font-size: 21px;
  box-shadow: 0 8px 18px rgba(0, 0, 0, .12);
}
.header-copy { position: relative; z-index: 1; min-width: 0; }
.page-eyebrow {
  display: block;
  margin-bottom: 5px;
  color: #d8bd86;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .2em;
  text-transform: uppercase;
}
.others-header h2 {
  color: #fff;
  font-size: clamp(24px, 2vw, 31px);
  font-weight: 700;
  letter-spacing: -.025em;
}
.others-header p {
  margin-top: 7px;
  color: rgba(255, 255, 255, .65);
  font-size: 12px;
}
.header-status {
  position: relative;
  z-index: 1;
  margin-left: auto;
  padding: 7px 11px;
  border: 1px solid rgba(232, 205, 153, .25);
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #e8dcc3;
  background: rgba(255, 255, 255, .06);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
}
.header-status i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9fd2aa;
  box-shadow: 0 0 0 4px rgba(159, 210, 170, .12);
}
.summary-strip {
  grid-template-columns: repeat(5, minmax(145px, 1fr));
  gap: 12px;
}
.summary-chip {
  position: relative;
  min-height: 100px;
  padding: 18px 15px 15px;
  border-color: var(--line);
  border-radius: 14px;
  overflow: hidden;
  background: linear-gradient(145deg, #fff 0%, #faf9f5 100%);
  box-shadow: 0 5px 16px rgba(44, 41, 34, .045);
  transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease;
}
.summary-chip:hover {
  border-color: #ccb98f;
  box-shadow: 0 10px 24px rgba(56, 49, 37, .08);
  transform: translateY(-2px);
}
.summary-chip > .b-icon {
  position: absolute;
  top: 15px;
  right: 15px;
  color: #b89659;
  font-size: 15px;
}
.summary-chip span {
  margin: 0 28px 10px 0;
  color: #8b806d;
  font-size: 9px;
  letter-spacing: .13em;
}
.summary-chip strong {
  color: var(--ink);
  font-size: 13px;
  font-weight: 750;
  line-height: 1.4;
}
.summary-chip.total-chip {
  border-color: #2f3a34;
  background: linear-gradient(145deg, #27302b, #39463e);
}
.summary-chip.total-chip span { color: #cbb992; }
.summary-chip.total-chip strong { color: #fff; font-size: 18px; }
.summary-chip.total-chip > .b-icon { color: #dfc58f; }
.collapse-card,
.detail-wrap,
.detail-loading {
  border-color: var(--line);
  border-radius: 15px;
  box-shadow: 0 7px 22px rgba(43, 40, 34, .05);
}
.collapse-strip {
  min-height: 62px;
  padding: 13px 18px;
  border-bottom-color: var(--line);
  color: var(--ink);
  background: linear-gradient(105deg, #f8f5ee, #fff 50%, #f4f1e9);
}
.collapse-strip:hover { background: #f8f5ed; }
.collapse-title { font-size: 14px; letter-spacing: .01em; }
.collapse-title .b-icon { color: var(--gold); }
.collapse-meta { color: var(--muted); }
.collapse-meta strong {
  color: #fff;
  background: var(--ink-soft);
}
.table-topbar { padding: 16px 18px; }
.table-topbar h5,
.detail-topbar h5 { color: var(--ink); }
.table-topbar p,
.detail-topbar p { color: var(--muted); line-height: 1.5; }
.mini-badge {
  color: var(--gold-deep);
  background: var(--gold-pale);
}
.summary-table th {
  padding: 11px 16px;
  color: #e9dcc4;
  background: #29322d;
  font-size: 10px;
  letter-spacing: .06em;
  text-transform: uppercase;
}
.summary-table td { padding: 13px 16px; border-color: #ebe8e0; }
.summary-table tbody tr:nth-child(even) { background: #fbfaf7; }
.summary-table tbody tr.active,
.summary-table tbody tr:hover { background: #f6f0e4; }
.type-cell { color: var(--ink-soft); }
.type-cell span { background: var(--gold); }
.count-btn.saved {
  border-color: #ddd8cd;
  color: var(--ink-soft);
  background: #faf9f6;
}
.count-btn.invalid {
  border-color: #e6cfaa;
  color: var(--gold-deep);
  background: #fffaf1;
}
.count-btn.saved.active,
.count-btn.saved:hover:not(:disabled) {
  border-color: var(--ink-soft);
  background: var(--ink-soft);
}
.count-btn.invalid.active,
.count-btn.invalid:hover:not(:disabled) {
  border-color: var(--gold);
  background: var(--gold);
}
.detail-table th {
  border-color: var(--line);
  color: var(--gold-deep);
  background: #f6f2e9;
}
.detail-table td { border-color: #ebe8e0; color: var(--ink-soft); }
.classified-rooms-section {
  border-color: #d7d1c3;
  border-radius: 18px;
  background: #f6f4ee;
  box-shadow: 0 18px 42px rgba(48, 44, 36, .09);
}
.classified-section-header {
  padding: 21px 22px;
  border-bottom-color: #dcd6c9;
  background:
    radial-gradient(circle at 90% 15%, rgba(177, 133, 60, .12), transparent 27%),
    linear-gradient(120deg, #fff 0%, #f8f5ee 62%, #f1eadb 100%);
}
.classified-heading-icon {
  color: #f0d9ab;
  background: linear-gradient(145deg, #252e29, #445248);
  box-shadow: 0 10px 22px rgba(35, 44, 39, .2);
}
.section-eyebrow { color: var(--gold); }
.classified-heading h3 { color: var(--ink); letter-spacing: -.02em; }
.classified-heading p { color: var(--muted); }
.classified-stats span {
  border-color: #d9d2c4;
  color: #7d7464;
  background: rgba(255, 255, 255, .7);
}
.classified-stats strong { color: var(--ink); }
.variant-list { padding: 16px; gap: 10px; }
.variant-card {
  border-color: #ddd7ca;
  border-radius: 12px;
  box-shadow: 0 5px 16px rgba(45, 41, 34, .05);
}
.variant-card:hover {
  border-color: #c9b58e;
  box-shadow: 0 10px 22px rgba(53, 47, 37, .09);
}
.variant-card.expanded {
  border-color: #b89558;
  box-shadow: 0 14px 30px rgba(79, 62, 36, .13);
}
.variant-trigger,
.variant-card.expanded .variant-trigger {
  min-height: 56px;
  color: #f9f6ef;
  background:
    linear-gradient(90deg, rgba(189, 149, 79, .16), transparent 34%),
    linear-gradient(110deg, #252e29 0%, #354139 56%, #3f4c43 100%);
}
.variant-card:not(.expanded) .variant-trigger:hover {
  background:
    linear-gradient(90deg, rgba(210, 171, 100, .2), transparent 40%),
    linear-gradient(110deg, #2d3731, #46544a);
}
.variant-toggle-icon {
  color: var(--gold-deep);
  background: #f7f0e2;
}
.variant-name { font-size: 13px; font-weight: 700; }
.variant-name strong { color: #dcc18c; }
.variant-panel { border-color: #ded8ca; }
.room-summary-list { padding: 11px; gap: 9px; background: #f1eee7; }
.room-summary-card { border-color: #ddd7cb; border-radius: 10px; }
.room-summary-card.expanded {
  border-color: #c6a66e;
  box-shadow: 0 8px 20px rgba(74, 58, 33, .1);
}
.room-summary-trigger {
  min-height: 49px;
  color: var(--ink);
  background: linear-gradient(100deg, #f9f7f1, #eee9de);
}
.room-summary-trigger:hover { background: #f5f0e5; }
.room-summary-card.expanded .room-summary-trigger {
  color: #fff;
  background: linear-gradient(105deg, #645238, #887049);
}
.summary-toggle-icon {
  border: 1px solid #d6c39f;
  color: var(--gold-deep);
  background: #fff;
}
.room-summary-trigger strong { color: var(--gold-deep); }
.room-summary-card.expanded .room-summary-trigger strong { color: #f0d69f; }
.room-summary-panel { border-color: #ddd7ca; background: #fff; }
.classified-table thead th {
  border-bottom-color: #b08a4f;
  color: #e8d5b2;
  background: #2d3731;
}
.classified-table tbody td {
  border-color: #e8e3da;
  color: #4f5652;
  background: #fff;
  font-weight: 500;
}
.classified-table tbody tr:nth-child(even) td { background: #faf9f6; }
.classified-table tbody tr:hover td { background: #f7f1e5; }
.classified-table tbody td:last-child strong { color: var(--gold-deep); }
.variant-pagination {
  border-color: #e3ded3;
  background: #faf9f6;
}
.variant-pagination button,
.variant-panel-state button,
.classified-state button {
  border-color: #d5cab5;
  color: var(--ink-soft);
}
.variant-pagination button:hover:not(:disabled) {
  color: #fff;
  border-color: var(--ink-soft);
  background: var(--ink-soft);
}
.variant-pagination span { color: var(--muted); }
.classified-state > .b-icon,
.variant-panel-state > .b-icon,
.empty-state .b-icon,
.page-error .b-icon { color: var(--gold); }
.classified-state strong,
.empty-state h5,
.page-error h4 { color: var(--ink); }
.variant-skeleton div,
.room-table-skeleton div {
  background: linear-gradient(90deg, #eae6dc 25%, #f8f6f0 50%, #eae6dc 75%);
  background-size: 200% 100%;
}
.page-error button { background: var(--ink-soft); }

@keyframes shimmer { 100% { transform: translateX(100%); } }
@keyframes loading-wave { 100% { background-position: -200% 0; } }
@media (max-width: 768px) {
  .others-page { padding: 12px; }
  .others-page-card { padding: 16px; }
  .page-toolbar { align-items: stretch; }
  .back-btn span,
  .regenerate-yaml-btn span,
  .manage-mapping-btn span { display: none; }
  .back-btn,
  .regenerate-yaml-btn,
  .manage-mapping-btn { width: 42px; padding: 0; }
  .table-topbar { align-items: flex-start; flex-direction: column; }
  .variant-trigger {
    grid-template-columns: 30px minmax(0, 1fr) 18px;
    gap: 8px;
  }
  .room-summary-trigger {
    grid-template-columns: 25px minmax(0, 1fr) 16px;
  }
  .variant-pagination { justify-content: center; }
}
@media (max-width: 1180px) {
  .summary-strip { grid-template-columns: repeat(3, minmax(180px, 1fr)); }
}
@media (max-width: 768px) {
  .others-page { padding: 12px 10px 26px; }
  .others-page-card { padding: 12px; border-radius: 17px; }
  .others-layout { gap: 15px; }
  .others-header { min-height: 96px; padding: 18px; }
  .header-monogram { width: 44px; height: 44px; flex-basis: 44px; }
  .header-status { display: none; }
  .summary-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .summary-chip { min-height: 92px; padding: 15px 12px 12px; }
  .summary-chip:nth-child(2),
  .summary-chip:nth-child(4) { grid-column: span 1; }
  .summary-chip.total-chip { grid-column: 1 / -1; min-height: 78px; }
  .collapse-strip { align-items: flex-start; flex-direction: column; gap: 8px; }
  .collapse-meta { width: 100%; justify-content: space-between; }
  .classified-section-header { align-items: flex-start; flex-direction: column; }
  .classified-stats { width: 100%; }
  .classified-stats span { flex: 1; }
  .classified-heading-icon { width: 42px; height: 42px; }
  .classified-heading h3 { font-size: 17px; }
}
@media (max-width: 430px) {
  .summary-strip { grid-template-columns: 1fr; }
  .summary-chip.total-chip { grid-column: auto; }
  .header-monogram { display: none; }
  .others-header h2 { font-size: 23px; }
  .classified-heading { align-items: flex-start; }
}

/* Project theme refinement: matches Room Mapping Data Management */
.others-page {
  --ink: #241b3f;
  --ink-soft: #453a59;
  --muted: #7d7589;
  --gold: #7c3aed;
  --gold-deep: #5b21b6;
  --gold-pale: #f0e9ff;
  --ivory: #fbfaff;
  --canvas: #f6f4fa;
  --line: #e5e0ed;
  background:
    radial-gradient(circle at 8% 5%, rgba(124, 58, 237, .055), transparent 25rem),
    radial-gradient(circle at 92% 18%, rgba(109, 40, 217, .045), transparent 30rem),
    #f7f6fa;
}
.back-btn {
  border-color: #ded8eb;
  color: #655d73;
  background: #fff;
  box-shadow: 0 4px 14px rgba(43, 26, 67, .05);
}
.back-btn:hover { color: #6d28d9; background: #faf7ff; }
.regenerate-yaml-btn {
  border-color: #cdbcea;
  color: #6d28d9;
  background: #fff;
  box-shadow: 0 6px 17px rgba(109, 40, 217, .09);
}
.regenerate-yaml-btn:hover {
  border-color: #a980e0;
  background: #f8f3ff;
}
.manage-mapping-btn {
  border-color: #6d28d9;
  background: #6d28d9;
  box-shadow: 0 9px 22px rgba(109, 40, 217, .22);
}
.manage-mapping-btn:hover {
  border-color: #5b21b6;
  background: #5b21b6;
  box-shadow: 0 12px 27px rgba(109, 40, 217, .28);
}
.others-page-card {
  border-color: #e1dce9;
  background: #fff;
  box-shadow: 0 22px 58px rgba(37, 23, 58, .1);
}
.others-header {
  min-height: 112px;
  border: 1px solid #e3dcef;
  color: #241b3f;
  background:
    radial-gradient(circle at 89% 25%, rgba(124, 58, 237, .1), transparent 22%),
    linear-gradient(115deg, #fff 0%, #fcfaff 58%, #f3ecff 100%);
  box-shadow: 0 10px 28px rgba(73, 45, 107, .075);
}
.others-header::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 5px;
  background: linear-gradient(180deg, #9333ea, #6d28d9);
}
.others-header::after {
  border-color: rgba(124, 58, 237, .1);
  box-shadow: 0 0 0 28px rgba(124, 58, 237, .028), 0 0 0 58px rgba(124, 58, 237, .018);
}
.header-monogram {
  border-color: #dfd1f5;
  color: #6d28d9;
  background: #f2eaff;
  box-shadow: 0 8px 18px rgba(109, 40, 217, .1);
}
.page-eyebrow { color: #7c3aed; }
.others-header h2 { color: #241b3f; }
.others-header p { color: #7d7589; }
.header-status {
  border-color: #d8c8ef;
  color: #5f398e;
  background: rgba(255, 255, 255, .8);
}
.header-status i {
  background: #21a663;
  box-shadow: 0 0 0 4px rgba(33, 166, 99, .12);
}
.summary-chip {
  border-color: #e3deeb;
  background: #fff;
  box-shadow: 0 5px 17px rgba(47, 28, 73, .045);
}
.summary-chip:hover {
  border-color: #cdb8ed;
  box-shadow: 0 10px 24px rgba(82, 47, 126, .1);
}
.summary-chip > .b-icon { color: #8b5cf6; }
.summary-chip span { color: #8b8396; }
.summary-chip strong { color: #2a213a; }
.summary-chip.total-chip {
  border-color: #d6c5ef;
  background: linear-gradient(145deg, #f4edff, #e9dcff);
}
.summary-chip.total-chip span { color: #6f4b99; }
.summary-chip.total-chip strong { color: #4c1d95; }
.summary-chip.total-chip > .b-icon { color: #6d28d9; }
.collapse-card,
.detail-wrap,
.detail-loading {
  border-color: #e2ddea;
  box-shadow: 0 7px 22px rgba(48, 29, 75, .05);
}
.collapse-strip {
  border-bottom-color: #e7e2ed;
  color: #2a213a;
  background: linear-gradient(105deg, #fbf9ff, #fff 50%, #f5f0fc);
}
.collapse-strip:hover { background: #f7f3fc; }
.collapse-title .b-icon { color: #7c3aed; }
.collapse-meta { color: #7d7589; }
.collapse-meta strong { background: #3b0764; }
.mini-badge { color: #5d25b2; background: #f0e9ff; }
.summary-table th {
  color: #fff;
  background: #3b0764;
}
.summary-table tbody tr.active,
.summary-table tbody tr:hover { background: #f6f0ff; }
.type-cell span { background: #8b5cf6; }
.count-btn.saved {
  border-color: #ded8eb;
  color: #5f5670;
  background: #fbfaff;
}
.count-btn.invalid {
  border-color: #decdf3;
  color: #6d28d9;
  background: #faf6ff;
}
.count-btn.saved.active,
.count-btn.saved:hover:not(:disabled) {
  border-color: #4c1d95;
  background: #4c1d95;
}
.count-btn.invalid.active,
.count-btn.invalid:hover:not(:disabled) {
  border-color: #7c3aed;
  background: #7c3aed;
}
.detail-table th {
  color: #5d25b2;
  background: #f5f0fc;
}
.classified-rooms-section {
  overflow: visible;
  border-color: #dfd8e9;
  background: #faf9fc;
  box-shadow: 0 18px 44px rgba(50, 29, 78, .1);
}
.classified-section-header {
  border-bottom-color: #e7e1ee;
  border-radius: 18px 18px 0 0;
  background:
    radial-gradient(circle at 90% 18%, rgba(124, 58, 237, .11), transparent 25%),
    linear-gradient(120deg, #fff 0%, #fbf8ff 62%, #f1e8ff 100%);
}
.classified-heading-icon {
  color: #fff;
  background: linear-gradient(145deg, #8b5cf6, #6d28d9);
  box-shadow: 0 10px 23px rgba(109, 40, 217, .24);
}
.section-eyebrow { color: #7c3aed; }
.classified-heading h3 { color: #241b3f; }
.classified-heading p { color: #7d7589; }
.classified-stats span {
  border-color: #ded5eb;
  color: #786d87;
  background: rgba(255, 255, 255, .88);
}
.classified-stats strong { color: #5b21b6; }
.variant-list {
  padding: 18px;
  gap: 12px;
  background: linear-gradient(180deg, #faf9fc, #f7f4fb);
}
.variant-card {
  overflow: hidden;
  border: 1px solid #e1dbe9;
  border-radius: 13px;
  background: #fff;
  box-shadow: 0 5px 15px rgba(43, 25, 67, .045);
}
.variant-card:hover {
  border-color: #cdb8ec;
  box-shadow: 0 10px 24px rgba(65, 36, 101, .1);
  transform: translateY(-1px);
}
.variant-card.expanded {
  border-color: #a77ae4;
  box-shadow: 0 14px 34px rgba(89, 43, 145, .15);
}
.variant-trigger,
.variant-card.expanded .variant-trigger {
  min-height: 64px;
  padding: 9px 15px;
  grid-template-columns: 36px minmax(0, 1fr) auto 20px;
  gap: 13px;
  color: #2b223c;
  background: #fff;
}
.variant-card:not(.expanded) .variant-trigger:hover {
  background: linear-gradient(90deg, #fbf9ff, #f6f0ff);
}
.variant-card.expanded .variant-trigger {
  color: #fff;
  background:
    radial-gradient(circle at 88% 20%, rgba(255, 255, 255, .16), transparent 22%),
    linear-gradient(105deg, #4c0d7d 0%, #6519a5 55%, #7c3aed 100%);
}
.variant-toggle-icon {
  width: 30px;
  height: 30px;
  border: 1px solid #e0d2f4;
  border-radius: 9px;
  color: #6d28d9;
  background: #f3ebff;
}
.variant-card.expanded .variant-toggle-icon {
  border-color: rgba(255, 255, 255, .28);
  color: #fff;
  background: rgba(255, 255, 255, .14);
}
.variant-name {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
  letter-spacing: 0;
}
.variant-name small,
.room-summary-name small {
  color: #968ca2;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: .1em;
  line-height: 1.15;
  text-transform: uppercase;
}
.variant-name > span {
  overflow: hidden;
  color: #2a213a;
  font-size: 13px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.variant-card.expanded .variant-name small { color: rgba(255, 255, 255, .58); }
.variant-card.expanded .variant-name > span { color: #fff; }
.variant-count {
  min-width: 88px;
  padding: 6px 10px;
  border: 1px solid #e3d8f1;
  border-radius: 9px;
  color: #7a6e88;
  background: #faf7ff;
  font-size: 9px;
  font-weight: 700;
  text-align: center;
}
.variant-count strong {
  margin-right: 3px;
  color: #6d28d9;
  font-size: 12px;
}
.variant-card.expanded .variant-count {
  border-color: rgba(255, 255, 255, .22);
  color: rgba(255, 255, 255, .68);
  background: rgba(255, 255, 255, .1);
}
.variant-card.expanded .variant-count strong { color: #fff; }
.variant-chevron { color: #8e839a; }
.variant-card.expanded .variant-chevron { color: #fff; }
.variant-panel {
  border-top-color: #e5ddec;
  background: #fbfaff;
}
.variant-panel-topbar {
  padding: 13px 16px;
  border-bottom-color: #e8e3ef;
  background: #faf8fd;
}
.variant-panel-topbar strong { color: #302744; }
.variant-panel-topbar span { color: #837b8f; }
.classification-key {
  padding: 5px 9px;
  border: 1px solid #ded3eb;
  border-radius: 999px;
  background: #fff;
  font-size: 9px !important;
  font-weight: 800;
}
.classification-key i { background: #8b5cf6; }
.room-summary-list {
  padding: 12px;
  gap: 9px;
  background: #f5f2f8;
}
.room-summary-card {
  border-color: #e1dbe8;
  border-radius: 11px;
  box-shadow: 0 3px 10px rgba(48, 30, 70, .035);
}
.room-summary-card.expanded {
  border-color: #b795df;
  box-shadow: 0 9px 22px rgba(82, 43, 128, .11);
}
.room-summary-trigger {
  min-height: 56px;
  grid-template-columns: 30px minmax(0, 1fr) auto 18px;
  color: #302744;
  background: #fff;
}
.room-summary-trigger:hover { background: #faf7ff; }
.room-summary-card.expanded .room-summary-trigger {
  color: #32134f;
  background: linear-gradient(100deg, #f5edff, #ebe0fb);
}
.summary-toggle-icon {
  border-color: #dfd3ee;
  border-radius: 8px;
  color: #6d28d9;
  background: #f7f2fd;
}
.room-summary-name {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}
.room-summary-name > span {
  overflow: hidden;
  color: #31263f;
  font-size: 12px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.room-count-badge {
  min-width: 72px;
  padding: 5px 9px;
  border-radius: 8px;
  color: #6222b1 !important;
  background: #f0e7fb;
  font-size: 9px;
  font-weight: 800 !important;
  text-align: center;
  white-space: nowrap;
}
.room-summary-panel {
  border-top-color: #ded3eb;
  background: #fff;
}
.classified-table thead th {
  border-bottom-color: #4c0d7d;
  color: #fff;
  background: #3b0764;
  font-size: 10px;
  letter-spacing: .04em;
  text-transform: uppercase;
}
.classified-table tbody td {
  border-color: #ebe7f0;
  color: #50475d;
  background: #fff;
}
.classified-table tbody tr:nth-child(even) td { background: #fcfbfd; }
.classified-table tbody tr:hover td { background: #f8f3ff; }
.classified-table tbody td:last-child strong {
  color: #6d28d9;
  font-size: 12px;
}
.variant-pagination {
  border-color: #e8e3ef;
  background: #fbfaff;
}
.variant-pagination button,
.variant-panel-state button,
.classified-state button {
  border-color: #ded4ea;
  color: #6d28d9;
}
.variant-pagination button:hover:not(:disabled) {
  border-color: #6d28d9;
  background: #6d28d9;
}
.variant-pagination span { color: #81788d; }
.classified-state > .b-icon,
.variant-panel-state > .b-icon,
.empty-state .b-icon,
.page-error .b-icon { color: #8b5cf6; }
.variant-skeleton div,
.room-table-skeleton div {
  background: linear-gradient(90deg, #f0ecf5 25%, #faf8fd 50%, #f0ecf5 75%);
  background-size: 200% 100%;
}
.page-error button { background: #6d28d9; }

@media (max-width: 768px) {
  .variant-trigger { grid-template-columns: 32px minmax(0, 1fr) 18px; }
  .variant-count { display: none; }
  .room-summary-trigger { grid-template-columns: 28px minmax(0, 1fr) 18px; }
  .room-count-badge { display: none; }
}

/* Room hierarchy and count clarity */
.classified-section-header { padding: 24px 26px; }
.classified-heading { gap: 16px; }
.classified-heading-icon {
  width: 50px;
  height: 50px;
  border-radius: 14px;
  font-size: 22px;
}
.classified-heading h3 {
  font-size: 22px;
  line-height: 1.2;
}
.classified-heading p { margin-top: 6px; font-size: 12px; }
.classified-stats span {
  min-width: 104px;
  padding: 9px 14px;
}
.classified-stats strong { font-size: 19px; }
.variant-list-heading {
  margin: 1px 2px 3px;
  padding: 2px 2px 10px;
  border-bottom: 1px solid #e6dfed;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}
.variant-list-heading > div > span {
  display: block;
  margin-bottom: 3px;
  color: #8b5cf6;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .12em;
  text-transform: uppercase;
}
.variant-list-heading h4 {
  margin: 0;
  color: #2a213a;
  font-size: 17px;
  font-weight: 800;
}
.variant-list-heading p {
  margin: 0;
  color: #83798f;
  font-size: 11px;
  font-weight: 650;
}
.variant-trigger,
.variant-card.expanded .variant-trigger {
  min-height: 72px;
  grid-template-columns: 40px minmax(0, 1fr) 118px 20px;
  padding: 10px 18px;
  gap: 15px;
}
.variant-toggle-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  font-size: 17px;
}
.variant-name > span {
  font-size: 15px;
  line-height: 1.35;
}
.variant-count {
  min-width: 118px;
  min-height: 42px;
  padding: 5px 10px 5px 6px;
  display: grid;
  grid-template-columns: 32px 1fr;
  align-items: center;
  gap: 7px;
  border-radius: 11px;
  font-size: 9px;
  line-height: 1.15;
  text-align: left;
}
.variant-count > strong {
  width: 30px;
  height: 30px;
  margin: 0;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: #7c3aed;
  font-size: 13px;
  box-shadow: 0 5px 12px rgba(109, 40, 217, .18);
}
.variant-count > span {
  color: #6f637b;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .025em;
}
.variant-card.expanded .variant-count > strong {
  color: #5b21b6;
  background: #fff;
  box-shadow: none;
}
.variant-card.expanded .variant-count > span { color: rgba(255, 255, 255, .82); }
.variant-panel-topbar { padding: 16px 18px; }
.variant-panel-topbar strong { font-size: 14px; }
.variant-panel-topbar > div > span { margin-top: 3px; font-size: 11px; }
.classification-key { padding: 7px 11px; font-size: 10px !important; }
.room-summary-list { padding: 14px; gap: 11px; }
.room-summary-trigger {
  min-height: 64px;
  grid-template-columns: 34px minmax(0, 1fr) 108px 18px;
  padding: 9px 15px;
  gap: 13px;
}
.summary-toggle-icon {
  width: 28px;
  height: 28px;
  font-size: 14px;
}
.room-summary-name > span {
  font-size: 14px;
  line-height: 1.35;
}
.room-count-badge {
  min-width: 108px;
  min-height: 38px;
  padding: 5px 9px 5px 6px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #dfd0f1;
  border-radius: 10px;
  color: #6b4d8d !important;
  background: #f7f1fd;
  font-size: 9px;
  text-align: left;
}
.room-count-badge > span {
  width: 27px;
  height: 27px;
  flex: 0 0 27px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: #6d28d9;
  font-size: 12px;
}
.classified-table thead th { padding: 12px 15px; }
.classified-table tbody td { padding: 15px; font-size: 12px; }
.language-picker {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #6f6380;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}
.language-picker select {
  min-width: 126px;
  height: 40px;
  padding: 0 28px 0 11px;
  border: 1px solid #d6c5ef;
  border-radius: 10px;
  outline: 0;
  color: #4c1d95;
  background: #fff;
  font-size: 11px;
  font-weight: 800;
  text-transform: none;
}
.language-picker select:focus { border-color: #8b5cf6; box-shadow: 0 0 0 3px rgba(124, 58, 237, .1); }

@media (max-width: 768px) {
  .classified-section-header { padding: 19px 17px; }
  .classified-heading-icon { width: 44px; height: 44px; }
  .classified-heading h3 { font-size: 18px; }
  .variant-list { padding: 11px; }
  .variant-list-heading { align-items: flex-start; flex-direction: column; gap: 5px; }
  .variant-trigger { min-height: 64px; grid-template-columns: 34px minmax(0, 1fr) auto 17px; padding: 9px 11px; gap: 9px; }
  .variant-count {
    min-width: 38px;
    width: 38px;
    min-height: 34px;
    padding: 3px;
    display: flex;
    justify-content: center;
  }
  .variant-count > strong { width: 29px; height: 29px; }
  .variant-count > span { display: none; }
  .room-summary-trigger { grid-template-columns: 28px minmax(0, 1fr) auto 16px; padding: 8px 10px; gap: 8px; }
  .room-count-badge {
    min-width: 35px;
    min-height: 33px;
    padding: 3px;
    display: inline-flex;
    color: transparent !important;
    font-size: 0;
  }
  .room-count-badge > span { width: 27px; height: 27px; }
}

.details-tabs {
  max-width: 1480px;
  min-height: 66px;
  margin: 0 auto 18px;
  padding: 7px;
  border: 1px solid rgba(124, 58, 237, .15);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: rgba(255, 255, 255, .86);
  box-shadow: 0 12px 30px rgba(63, 27, 91, .07);
  backdrop-filter: blur(12px);
}
.tab-buttons {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(150px, 230px));
  gap: 5px;
}
.tab-buttons button {
  min-height: 50px;
  padding: 0 18px;
  border: 0;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  position: relative;
  color: #786a82;
  background: transparent;
  font-size: 12px;
  font-weight: 850;
  transition: color .2s ease, background .2s ease, box-shadow .2s ease;
}
.tab-buttons button::after {
  content: '';
  position: absolute;
  right: 25%;
  bottom: 0;
  left: 25%;
  height: 2px;
  border-radius: 2px;
  background: transparent;
}
.tab-buttons button.active {
  color: #5b21b6;
  background: linear-gradient(135deg, #fbf7ff, #f2e4ff);
  box-shadow: inset 0 0 0 1px rgba(124, 58, 237, .12);
}
.tab-buttons button.active::after { background: #a21caf; }
.tab-language-picker {
  margin: 0 5px 0 0;
  display: flex;
  align-items: center;
  gap: 10px;
}
.tab-language-picker > span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #796b82;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: .06em;
  text-transform: uppercase;
}
.tab-language-picker select {
  min-width: 145px;
  height: 42px;
  padding: 0 30px 0 12px;
  border: 1px solid #ddccef;
  border-radius: 10px;
  outline: 0;
  color: #4c1d95;
  background: #fff;
  font-size: 11px;
  font-weight: 800;
}
.tab-language-picker select:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, .1);
}
.hotel-tab-card {
  width: 100%;
  max-width: none;
  min-height: 420px;
  margin: 0 auto;
}
.others-page { width: 100%; box-sizing: border-box; }
.page-toolbar,
.details-tabs,
.others-page-card {
  width: 100%;
  max-width: none;
  box-sizing: border-box;
}
@media (max-width: 680px) {
  .details-tabs { padding: 7px; align-items: stretch; flex-direction: column; gap: 8px; }
  .tab-buttons { width: 100%; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .tab-buttons button { min-width: 0; padding: 0 10px; }
  .tab-language-picker { margin: 0; padding: 0 4px 4px; justify-content: space-between; }
  .tab-language-picker select { min-width: 0; width: 160px; }
}

/* Bizinso brand system — Brand Guidelines 01.12.2021 */
.others-page {
  --brand-cyan: #2db8c7;
  --brand-blue: #0097da;
  --brand-navy: #4d5ba4;
  --brand-gray: #9d9e9e;
  --brand-yellow: #ffd700;
  --brand-orange: #ff8000;
  --brand-red: #e14d4d;
  --brand-ink: #26324d;
  --brand-muted: #68738b;
  --brand-line: #dce6ee;
  padding: clamp(14px, 2.1vw, 30px);
  color: var(--brand-ink);
  font-family: 'Montserrat', sans-serif;
  background:
    radial-gradient(circle at 4% 2%, rgba(45, 184, 199, .11), transparent 24rem),
    radial-gradient(circle at 96% 14%, rgba(77, 91, 164, .08), transparent 30rem),
    #f5f8fb;
}
.page-toolbar {
  min-height: 112px;
  margin-bottom: 18px;
  padding: 18px 22px;
  border: 1px solid var(--brand-line);
  border-radius: 18px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 20px;
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at 74% -45%, rgba(45, 184, 199, .2), transparent 38%),
    linear-gradient(112deg, #fff 0%, #fff 60%, #f1f8fc 60%, #edf4fb 100%);
  box-shadow: 0 16px 40px rgba(57, 76, 112, .09);
}
.page-toolbar::before {
  content: '';
  width: 5px;
  position: absolute;
  inset: 0 auto 0 0;
  background: linear-gradient(180deg, var(--brand-cyan), var(--brand-blue) 60%, var(--brand-navy));
}
.back-btn {
  align-self: flex-start;
  border-color: #cedbe5;
  color: var(--brand-navy);
  background: #fff;
  box-shadow: 0 5px 14px rgba(77, 91, 164, .08);
}
.back-btn:hover {
  border-color: var(--brand-cyan);
  color: #fff;
  background: var(--brand-cyan);
}
.toolbar-heading { min-width: 0; }
.toolbar-heading > span {
  display: block;
  margin-bottom: 5px;
  color: var(--brand-blue);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .16em;
  text-transform: uppercase;
}
.toolbar-heading h1 {
  margin: 0;
  color: var(--brand-ink);
  font-size: clamp(24px, 2.3vw, 36px);
  font-weight: 600;
  letter-spacing: -.035em;
}
.toolbar-heading p {
  margin: 7px 0 0;
  color: var(--brand-muted);
  font-size: 12px;
  font-weight: 400;
}
.brand-signature {
  width: 220px;
  height: 86px;
  padding: 8px 12px;
  border: 1px solid #e2e8ee;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  background: #fff;
}
.brand-signature img { width: 196px; height: 70px; display: block; object-fit: contain; }
.details-tabs {
  min-height: 76px;
  margin-bottom: 20px;
  padding: 8px;
  border-color: rgba(0, 151, 218, .18);
  border-radius: 17px;
  background: rgba(255, 255, 255, .94);
  box-shadow: 0 13px 32px rgba(51, 77, 118, .08);
}
.tab-buttons { grid-template-columns: repeat(3, minmax(150px, 220px)); }
.tab-buttons button {
  min-height: 58px;
  justify-content: flex-start;
  color: #69758a;
  font-weight: 500;
}
.tab-buttons button > .b-icon {
  width: 34px;
  height: 34px;
  padding: 9px;
  border-radius: 10px;
  color: var(--brand-navy);
  background: #eef2fa;
}
.tab-buttons button > span { display: flex; flex-direction: column; gap: 2px; text-align: left; }
.tab-buttons button strong { color: inherit; font-size: 12px; font-weight: 700; }
.tab-buttons button small { color: #929bad; font-size: 8px; font-weight: 500; letter-spacing: .04em; }
.tab-buttons button.active {
  color: #fff;
  background: linear-gradient(110deg, var(--brand-navy) 0%, var(--brand-navy) 60%, var(--brand-blue) 100%);
  box-shadow: 0 9px 22px rgba(77, 91, 164, .22);
}
.tab-buttons button.active > .b-icon { color: var(--brand-blue); background: #fff; }
.tab-buttons button.active small { color: rgba(255, 255, 255, .7); }
.tab-buttons button.active::after { background: var(--brand-yellow); }
.tab-language-picker > span { color: var(--brand-muted); }
.tab-language-picker select {
  border-color: #cedde8;
  color: var(--brand-navy);
}
.tab-language-picker select:focus {
  border-color: var(--brand-cyan);
  box-shadow: 0 0 0 3px rgba(45, 184, 199, .14);
}
.others-page-card {
  border-color: var(--brand-line);
  border-radius: 20px;
  background: rgba(255, 255, 255, .97);
  box-shadow: 0 20px 52px rgba(47, 71, 107, .1);
}
.others-header {
  border-color: rgba(77, 91, 164, .2);
  color: #fff;
  background:
    radial-gradient(circle at 88% 12%, rgba(45, 184, 199, .32), transparent 26%),
    linear-gradient(112deg, var(--brand-navy) 0%, var(--brand-navy) 60%, var(--brand-blue) 100%);
  box-shadow: 0 14px 34px rgba(77, 91, 164, .22);
}
.others-header::before { background: var(--brand-cyan); }
.others-header::after { border-color: rgba(255, 255, 255, .12); }
.header-monogram {
  border-color: rgba(255, 255, 255, .24);
  color: #fff;
  background: rgba(255, 255, 255, .12);
}
.page-eyebrow { color: #b9f3f5; }
.others-header h2 { color: #fff; }
.others-header p { color: rgba(255, 255, 255, .72); }
.header-status {
  border-color: rgba(255, 255, 255, .22);
  color: #fff;
  background: rgba(255, 255, 255, .1);
}
.header-status i { background: var(--brand-yellow); box-shadow: 0 0 0 4px rgba(255, 215, 0, .16); }
.summary-chip {
  border-color: var(--brand-line);
  background: linear-gradient(145deg, #fff, #f8fbfd);
  box-shadow: 0 6px 18px rgba(56, 77, 111, .05);
}
.summary-chip:hover { border-color: rgba(45, 184, 199, .55); box-shadow: 0 12px 26px rgba(45, 184, 199, .1); }
.summary-chip > .b-icon { color: var(--brand-blue); }
.summary-chip span { color: #7b879a; }
.summary-chip strong { color: var(--brand-ink); }
.summary-chip.total-chip {
  border-color: rgba(45, 184, 199, .4);
  background: linear-gradient(145deg, rgba(45, 184, 199, .13) 0 60%, rgba(0, 151, 218, .13));
}
.summary-chip.total-chip span { color: #387986; }
.summary-chip.total-chip strong { color: var(--brand-navy); }
.summary-chip.total-chip > .b-icon { color: var(--brand-cyan); }
.collapse-card,
.detail-wrap,
.detail-loading { border-color: var(--brand-line); box-shadow: 0 8px 23px rgba(55, 74, 106, .05); }
.collapse-strip { color: var(--brand-ink); background: linear-gradient(105deg, #f6fbfc, #fff 60%, #f3f6fb); }
.collapse-title .b-icon { color: var(--brand-cyan); }
.collapse-meta strong { background: var(--brand-navy); }
.mini-badge { color: var(--brand-navy); background: #edf2fb; }
.summary-table th,
.classified-table thead th { color: #fff; background: var(--brand-navy); border-bottom-color: var(--brand-cyan); }
.summary-table tbody tr.active,
.summary-table tbody tr:hover,
.classified-table tbody tr:hover td { background: #eef9fb; }
.type-cell span { background: var(--brand-cyan); }
.count-btn.saved.active,
.count-btn.saved:hover:not(:disabled) { border-color: var(--brand-navy); background: var(--brand-navy); }
.count-btn.invalid { border-color: rgba(255, 128, 0, .32); color: #a85b08; background: #fff8ef; }
.count-btn.invalid.active,
.count-btn.invalid:hover:not(:disabled) { border-color: var(--brand-orange); background: var(--brand-orange); }
.detail-table th { color: var(--brand-navy); background: #eff5fa; }
.classified-rooms-section { border-color: var(--brand-line); background: #f8fafc; box-shadow: 0 18px 44px rgba(47, 69, 104, .1); }
.classified-section-header {
  border-bottom-color: var(--brand-line);
  background:
    radial-gradient(circle at 90% 18%, rgba(45, 184, 199, .13), transparent 25%),
    linear-gradient(120deg, #fff 0%, #fff 60%, #eef6fb 100%);
}
.classified-heading-icon {
  background: linear-gradient(145deg, var(--brand-cyan) 0%, var(--brand-cyan) 60%, var(--brand-blue) 100%);
  box-shadow: 0 10px 23px rgba(0, 151, 218, .22);
}
.section-eyebrow { color: var(--brand-blue); }
.classified-heading h3,
.classified-stats strong,
.variant-list-heading h4 { color: var(--brand-ink); }
.variant-list-heading > div > span { color: var(--brand-cyan); }
.variant-list { background: linear-gradient(180deg, #fafcfd, #f4f7fa); }
.variant-card { border-color: var(--brand-line); box-shadow: 0 5px 15px rgba(44, 65, 98, .05); }
.variant-card:hover { border-color: rgba(45, 184, 199, .6); box-shadow: 0 10px 24px rgba(45, 184, 199, .1); }
.variant-card.expanded { border-color: var(--brand-blue); box-shadow: 0 14px 34px rgba(0, 151, 218, .14); }
.variant-card:not(.expanded) .variant-trigger:hover { background: linear-gradient(90deg, #f7fcfd, #eff8fb); }
.variant-card.expanded .variant-trigger {
  background:
    radial-gradient(circle at 88% 20%, rgba(255, 255, 255, .15), transparent 22%),
    linear-gradient(105deg, var(--brand-navy) 0%, var(--brand-navy) 60%, var(--brand-blue) 100%);
}
.variant-toggle-icon,
.summary-toggle-icon { border-color: #cfe3ea; color: var(--brand-blue); background: #edf9fa; }
.variant-name > span,
.room-summary-name > span { color: var(--brand-ink); }
.variant-count { border-color: #d5e3eb; color: var(--brand-muted); background: #f6fbfc; }
.variant-count strong,
.variant-count > strong,
.room-count-badge > span { color: #fff; background: var(--brand-blue); box-shadow: 0 5px 12px rgba(0, 151, 218, .18); }
.room-summary-list { background: #f1f5f8; }
.room-summary-card { border-color: var(--brand-line); }
.room-summary-card.expanded { border-color: var(--brand-cyan); box-shadow: 0 9px 22px rgba(45, 184, 199, .11); }
.room-summary-trigger:hover,
.room-summary-card.expanded .room-summary-trigger { color: var(--brand-ink); background: linear-gradient(100deg, #f1fbfc, #e8f5f8); }
.room-count-badge { border-color: #cfe3ea; color: var(--brand-navy) !important; background: #eff8fa; }
.classified-table tbody td { border-color: #e5edf2; color: #526078; }
.classified-table tbody tr:nth-child(even) td { background: #f9fbfc; }
.classified-table tbody td:last-child strong { color: var(--brand-blue); }
.variant-pagination button,
.variant-panel-state button,
.classified-state button { border-color: #cadce7; color: var(--brand-navy); }
.variant-pagination button:hover:not(:disabled),
.page-error button { border-color: var(--brand-blue); background: var(--brand-blue); }
.classified-state > .b-icon,
.variant-panel-state > .b-icon,
.empty-state .b-icon,
.page-error .b-icon { color: var(--brand-cyan); }

@media (max-width: 760px) {
  .page-toolbar { min-height: auto; padding: 16px; grid-template-columns: auto minmax(0, 1fr); gap: 13px; }
  .toolbar-heading p,
  .brand-signature { display: none; }
  .toolbar-heading h1 { font-size: 20px; }
  .back-btn { align-self: center; }
  .details-tabs { border-radius: 14px; }
  .tab-buttons { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .tab-buttons button { padding: 0 6px; }
  .tab-buttons button > .b-icon { display: none; }
  .tab-buttons button { min-height: 54px; }
}

/* Sellable multi-room options */
.room-options {
  max-height: 680px;
  padding: 16px;
  overflow: auto;
  display: grid;
  gap: 14px;
  background: #f4f7fa;
}
.room-option-card {
  overflow: hidden;
  border: 1px solid #dbe5ec;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 7px 20px rgba(45, 61, 89, .07);
  transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease;
}
.room-option-card:hover {
  border-color: rgba(45, 184, 199, .72);
  box-shadow: 0 13px 30px rgba(45, 105, 131, .12);
  transform: translateY(-1px);
}
.option-header {
  min-height: 58px;
  padding: 10px 16px;
  border-bottom: 1px solid #e7edf2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(105deg, #f7fbfc, #fff 58%, #eff7fb);
}
.option-header > div {
  display: flex;
  align-items: center;
  gap: 11px;
}
.option-number {
  padding: 7px 10px;
  border-radius: 8px;
  color: #fff;
  background: var(--brand-navy);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .06em;
  text-transform: uppercase;
}
.option-header strong { color: var(--brand-ink); font-size: 12px; }
.supplier-reference {
  max-width: 45%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #78849a;
  font-size: 10px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.supplier-reference .b-icon { color: var(--brand-blue); }
.option-rooms { padding: 0 16px; }
.option-room-row {
  min-height: 78px;
  padding: 12px 0;
  border-bottom: 1px dashed #e3e9ee;
  display: grid;
  grid-template-columns: 30px minmax(220px, 1.7fr) minmax(145px, .8fr) minmax(130px, .75fr) minmax(105px, .55fr);
  align-items: center;
  gap: 14px;
}
.option-room-row:last-child { border-bottom: 0; }
.room-sequence {
  width: 28px;
  height: 28px;
  border: 1px solid #cfe3ea;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--brand-blue);
  background: #eef9fa;
  font-size: 11px;
  font-weight: 900;
}
.option-room-copy { min-width: 0; }
.option-room-copy strong {
  overflow: hidden;
  display: block;
  color: #303a50;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.room-occupancy {
  overflow: hidden;
  display: block;
  margin-top: 4px;
  color: #9098a8;
  font-size: 9px;
  font-weight: 650;
  text-overflow: ellipsis;
  text-transform: capitalize;
  white-space: nowrap;
}
.room-attributes {
  margin-top: 7px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.room-attribute {
  max-width: 100%;
  padding: 4px 7px;
  border: 1px solid #dbe5ee;
  border-radius: 6px;
  overflow: hidden;
  color: #53657c;
  background: #f5f8fb;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.room-attribute b {
  margin-right: 4px;
  color: #33465f;
  font-weight: 850;
}
.bed-attribute { border-color: #d9d8f2; background: #f7f6ff; }
.view-attribute { border-color: #cfe7ec; background: #f1fafb; }
.meal-pill,
.policy-pill {
  width: max-content;
  max-width: 100%;
  padding: 6px 9px;
  border-radius: 999px;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #16704b;
  background: #eaf9f1;
  font-size: 9px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.meal-pill .b-icon { color: #26a269; }
.policy-pill { color: #9a6210; background: #fff6e8; }
.policy-pill.refundable { color: #16704b; background: #ebfaf2; }
.policy-pill i {
  width: 6px;
  height: 6px;
  flex: 0 0 6px;
  border-radius: 50%;
  background: #f59e0b;
}
.policy-pill.refundable i { background: #22c55e; }
.individual-rate {
  justify-self: end;
  color: #4d5ba4;
  font-size: 12px;
  font-weight: 850;
  white-space: nowrap;
}
.option-footer {
  min-height: 62px;
  padding: 10px 16px;
  border-top: 1px solid #e1e8ee;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  background: linear-gradient(100deg, #fbfcfd, #f0f6fb);
}
.option-footer span {
  color: #7c879a;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .05em;
  text-transform: uppercase;
}
.option-footer strong {
  min-width: 130px;
  color: var(--brand-blue);
  font-size: 18px;
  font-weight: 850;
  text-align: right;
}

@media (max-width: 980px) {
  .option-room-row {
    grid-template-columns: 30px minmax(190px, 1fr) auto auto;
  }
  .individual-rate { grid-column: 4; }
  .policy-pill { grid-column: 3; }
  .meal-pill { display: none; }
}
@media (max-width: 620px) {
  .room-options { padding: 10px; }
  .option-header { align-items: flex-start; flex-direction: column; gap: 8px; }
  .supplier-reference { max-width: 100%; }
  .option-rooms { padding: 0 12px; }
  .option-room-row {
    grid-template-columns: 28px minmax(0, 1fr) auto;
    gap: 9px;
  }
  .option-room-copy { grid-column: 2 / -1; }
  .room-sequence { grid-row: 1 / 3; }
  .policy-pill { grid-column: 2; }
  .individual-rate { grid-column: 3; }
  .option-footer { justify-content: space-between; }
}
</style>
